
import React, { useState, useMemo, useEffect } from 'react';
import { useApp, Appointment, User, DEFAULT_WORKING_HOURS, WorkingHours, MedicalRecord, getTodayDateString, parseRecordTimestamp, getSafeUrl, formatHumaneRovitaLicenseId, isUserDisabled } from './AppContext';
import { SectionTitle, Button } from './Components';
import { LiveCallNotificationBanner } from './LiveCallNotificationBanner';
import { useActiveCallStatus, ParticipantStatusBadge, getParticipantOnlineStatus } from './useActiveCallStatus';
import { PreConsultationTriageModal } from './PreConsultationTriageModal';
import { DigitalPrescriptionModal } from './DigitalPrescriptionModal';
import { MedicalRecordMetadataModal } from './MedicalRecordMetadataModal';
import { safeStringify } from './src/utils/safeJson';

const normalizeDate = (d: string) => {
  if (!d) return '';
  const parts = d.trim().split('-');
  if (parts.length === 3) {
    return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
  }
  return d.trim();
};

const normalizeTime = (t: string) => {
  if (!t) return '';
  const parts = t.trim().split(':');
  if (parts.length === 2) {
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
  }
  return t.trim();
};

interface VisualAvailabilityCalendarProps {
  user: User | null;
  busyDate: string;
  setBusyDate: (d: string) => void;
  allAppointments: Appointment[];
  appointments?: Appointment[];
  toggleDoctorBusySlot: (date: string, time: string, reason?: string, slotType?: 'busy' | 'open') => Promise<void>;
  setDoctorDayBusy: (date: string, isBusy: boolean, slotType?: 'busy' | 'open') => Promise<void>;
}

const VisualAvailabilityCalendar: React.FC<VisualAvailabilityCalendarProps> = ({
  user,
  busyDate,
  setBusyDate,
  allAppointments,
  appointments = [],
  toggleDoctorBusySlot,
  setDoctorDayBusy
}) => {
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const initial = busyDate ? new Date(busyDate + 'T00:00:00') : new Date();
    return isNaN(initial.getTime()) ? new Date() : new Date(initial.getFullYear(), initial.getMonth(), 1);
  });

  useEffect(() => {
    if (busyDate) {
      const d = new Date(busyDate + 'T00:00:00');
      if (!isNaN(d.getTime())) {
        setCalendarMonth(new Date(d.getFullYear(), d.getMonth(), 1));
      }
    }
  }, [busyDate]);

  const year = calendarMonth.getFullYear();
  const month = calendarMonth.getMonth();

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const totalDaysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();

  const prevMonth = () => {
    setCalendarMonth(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCalendarMonth(new Date(year, month + 1, 1));
  };

  const jumpToToday = () => {
    const todayStr = getTodayDateString();
    setBusyDate(todayStr);
    const today = new Date();
    setCalendarMonth(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const selectedDateObj = new Date(busyDate + 'T00:00:00');
  const selectedDayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][selectedDateObj.getDay()] || 'Monday';
  const selectedDaySchedule = user?.workingHours?.[selectedDayName];
  const isSelectedDayOff = selectedDaySchedule ? selectedDaySchedule.active === false : false;

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Visual Calendar Box */}
      <div className="p-6 md:p-8 bg-white rounded-[2.5rem] border border-slate-200/80 shadow-sm space-y-6">
        {/* Header with Month Nav */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-100">
          <div>
            <div className="flex items-center gap-3">
              <h3 className="text-2xl font-black text-slate-900 font-['Lora',_serif]">
                {monthNames[month]} {year}
              </h3>
              <button 
                onClick={jumpToToday}
                className="px-3 py-1 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-xl text-xs font-bold transition-all shadow-xs"
              >
                Today
              </button>
            </div>
            <p className="text-slate-500 text-xs font-medium mt-0.5">
              Visual Availability & Schedule Calendar. Click any date to manage availability and slots.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={prevMonth}
              className="p-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-2xl transition-all font-bold text-sm"
              title="Previous Month"
            >
              <i className="fa-solid fa-chevron-left"></i>
            </button>
            <button
              onClick={nextMonth}
              className="p-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-2xl transition-all font-bold text-sm"
              title="Next Month"
            >
              <i className="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 text-xs font-bold pt-1">
          <span className="flex items-center gap-1.5 text-slate-700">
            <span className="w-3.5 h-3.5 rounded-md bg-white border-2 border-slate-400 inline-block shadow-xs"></span> Active Working Day
          </span>
          <span className="flex items-center gap-1.5 text-slate-600">
            <span className="w-3.5 h-3.5 rounded-md bg-slate-200 border border-slate-300 inline-block"></span> Non-Working (Off)
          </span>
          <span className="flex items-center gap-1.5 text-amber-800">
            <span className="w-3.5 h-3.5 rounded-md bg-amber-400 inline-block shadow-xs"></span> Has Busy Slots
          </span>
          <span className="flex items-center gap-1.5 text-emerald-800">
            <span className="w-3.5 h-3.5 rounded-md bg-emerald-500 inline-block shadow-xs"></span> Custom Open Slots
          </span>
          <span className="flex items-center gap-1.5 text-indigo-800">
            <span className="w-3.5 h-3.5 rounded-md bg-indigo-600 inline-block shadow-xs"></span> Appointments
          </span>
        </div>

        {/* Calendar Grid - Guaranteed 7 Columns */}
        <div 
          className="w-full gap-1.5 md:gap-2.5 pt-2"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}
        >
          {dayNames.map(dName => (
            <div key={dName} className="text-center py-2 font-black text-slate-500 text-xs md:text-sm uppercase tracking-wider">
              {dName}
            </div>
          ))}

          {Array.from({ length: firstDayOfWeek }).map((_, idx) => {
            const pDay = prevMonthDays - firstDayOfWeek + idx + 1;
            return (
              <div 
                key={`prev-${idx}`} 
                className="p-2 min-h-[65px] md:min-h-[85px] bg-slate-50/50 rounded-2xl border border-dashed border-slate-200/50 text-slate-300 text-xs font-bold flex flex-col justify-between opacity-40 pointer-events-none"
              >
                <span>{pDay}</span>
              </div>
            );
          })}

          {Array.from({ length: totalDaysInMonth }).map((_, idx) => {
            const dayNum = idx + 1;
            const dateStr = `${year}-${(month + 1).toString().padStart(2, '0')}-${dayNum.toString().padStart(2, '0')}`;
            const dateObj = new Date(`${dateStr}T00:00:00`);
            const dName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dateObj.getDay()];
            const sched = user?.workingHours?.[dName];
            const isOffDay = sched ? sched.active === false : false;

            const isSelected = busyDate === dateStr;
            const todayStr = getTodayDateString();
            const isToday = todayStr === dateStr;

            const normDateStr = normalizeDate(dateStr);
            const doctorAppointmentsList = (allAppointments && allAppointments.length > 0) ? allAppointments : (appointments || []);
            const dayAppointments = doctorAppointmentsList.filter(a => a.doctorId === user?.id && normalizeDate(a.date) === normDateStr && a.status !== 'cancelled');
            const dayBusySlots = user?.busySlots?.filter(b => normalizeDate(b.date) === normDateStr && (b.type === 'busy' || !b.type)) || [];
            const dayOpenSlots = user?.busySlots?.filter(b => normalizeDate(b.date) === normDateStr && b.type === 'open') || [];

            return (
              <button
                key={dateStr}
                onClick={() => setBusyDate(dateStr)}
                className={`p-2 min-h-[75px] md:min-h-[90px] rounded-2xl border text-left flex flex-col justify-between transition-all relative group ${
                  isSelected
                    ? 'ring-2 ring-indigo-600 border-indigo-500 bg-indigo-50/90 shadow-md scale-[1.02] z-10'
                    : isOffDay
                    ? dayOpenSlots.length > 0
                      ? 'bg-emerald-50/70 border-emerald-200 hover:border-emerald-400'
                      : 'bg-slate-100/90 border-slate-200/80 text-slate-500 hover:bg-slate-200/80'
                    : dayBusySlots.length > 0
                    ? 'bg-amber-50/70 border-amber-200/80 hover:border-amber-400'
                    : 'bg-white border-slate-200/80 hover:border-indigo-300 hover:bg-indigo-50/20'
                }`}
              >
                <div className="flex items-center justify-between w-full">
                  <span className={`text-xs md:text-sm font-black ${isToday ? 'w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center -ml-1 -mt-1 shadow-xs' : isSelected ? 'text-indigo-900 font-extrabold' : 'text-slate-700'}`}>
                    {dayNum}
                  </span>
                  {isOffDay && (
                    <span className="text-[9px] font-black tracking-wider uppercase px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">
                      OFF
                    </span>
                  )}
                </div>

                <div className="space-y-1 mt-1 w-full">
                  {dayAppointments.length > 0 && (
                    <div className="text-[8px] md:text-[9px] font-extrabold px-1.5 py-0.5 rounded-lg bg-indigo-600 text-white truncate shadow-xs">
                      {dayAppointments.length} Booked
                    </div>
                  )}

                  {isOffDay && dayOpenSlots.length > 0 && (
                    <div className="text-[8px] md:text-[9px] font-extrabold px-1.5 py-0.5 rounded-lg bg-emerald-600 text-white truncate shadow-xs">
                      {dayOpenSlots.length} Open
                    </div>
                  )}

                  {!isOffDay && dayBusySlots.length > 0 && (
                    <div className="text-[8px] md:text-[9px] font-extrabold px-1.5 py-0.5 rounded-lg bg-amber-500 text-white truncate shadow-xs">
                      {dayBusySlots.length} Busy
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected Date Slot Management Panel */}
      <div className="p-6 md:p-8 bg-slate-50/80 rounded-[2.5rem] border border-slate-200/80 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-200">
          <div>
            <div className="flex items-center gap-2">
              <span className={`px-2.5 py-1 rounded-xl text-xs font-black uppercase tracking-wider ${isSelectedDayOff ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                {isSelectedDayOff ? 'Non-Working Day (OFF)' : 'Active Working Day'}
              </span>
              <span className="text-slate-400 text-xs font-bold">• {selectedDayName}</span>
            </div>
            <h4 className="text-xl font-bold text-slate-900 font-['Lora',_serif] mt-1">
              Custom Date Availability & Busy Slots: <span className="text-indigo-600 font-black">{busyDate}</span>
            </h4>
            <p className="text-slate-500 text-xs font-medium mt-1">
              {isSelectedDayOff
                ? 'This date falls on your non-working day. Click any time slot to set it as OPEN for custom patient bookings.'
                : 'This date is an active working day. Click any time slot to mark it as BUSY / BLOCKED.'}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Date:</label>
              <input 
                type="date" 
                className="px-4 py-2 bg-white rounded-xl border border-slate-200 font-bold text-sm text-slate-700 outline-none focus:border-indigo-500 shadow-sm"
                value={busyDate}
                min={new Date().toISOString().split('T')[0]}
                onChange={e => setBusyDate(e.target.value)}
              />
            </div>

            {isSelectedDayOff ? (
              <Button 
                variant="outline"
                className="py-2 px-3 text-xs bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                onClick={() => setDoctorDayBusy(busyDate, true, 'open')}
              >
                <i className="fa-solid fa-calendar-plus mr-1.5"></i> Open All Standard Slots
              </Button>
            ) : (
              <Button 
                variant="outline"
                className="py-2 px-3 text-xs bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
                onClick={() => setDoctorDayBusy(busyDate, true, 'busy')}
              >
                <i className="fa-solid fa-ban mr-1.5"></i> Block Entire Day
              </Button>
            )}

            <Button 
              variant="ghost"
              className="py-2 px-3 text-xs text-slate-500 hover:bg-slate-200/60"
              onClick={() => setDoctorDayBusy(busyDate, false)}
            >
              Clear Custom Settings
            </Button>
          </div>
        </div>

        {/* Time Slot Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2.5">
          {['08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30'].map(slot => {
            const normBusyDate = normalizeDate(busyDate);
            const normSlot = normalizeTime(slot);
            const doctorAppointmentsList = (allAppointments && allAppointments.length > 0) ? allAppointments : (appointments || []);
            const isBooked = doctorAppointmentsList.some(a => a.doctorId === user?.id && normalizeDate(a.date) === normBusyDate && normalizeTime(a.time) === normSlot && a.status !== 'cancelled');
            const slotObj = user?.busySlots?.find(b => normalizeDate(b.date) === normBusyDate && normalizeTime(b.time) === normSlot);
            const isExplicitBusy = slotObj && (slotObj.type === 'busy' || !slotObj.type);
            const isExplicitOpen = slotObj && slotObj.type === 'open';

            const schedStart = selectedDaySchedule?.start || '09:00';
            const schedEnd = selectedDaySchedule?.end || '17:00';
            const isStandardShift = !isSelectedDayOff && slot >= schedStart && slot < schedEnd;

            if (isBooked) {
              return (
                <div
                  key={slot}
                  className="p-3 rounded-2xl text-xs font-bold flex flex-col items-center gap-1 shadow-sm bg-rose-100 border border-rose-200 text-rose-700 cursor-not-allowed"
                >
                  <span className="text-sm font-black">{slot}</span>
                  <span className="text-[8px] uppercase tracking-wider font-extrabold px-1.5 py-0.5 rounded bg-rose-200 text-rose-800">
                    Booked
                  </span>
                </div>
              );
            }

            if (isSelectedDayOff) {
              return (
                <button
                  key={slot}
                  onClick={() => toggleDoctorBusySlot(busyDate, slot, 'Doctor opened slot', 'open')}
                  className={`p-3 rounded-2xl text-xs font-bold transition-all flex flex-col items-center gap-1 shadow-sm ${
                    isExplicitOpen
                      ? 'bg-emerald-600 text-white shadow-emerald-600/20 hover:bg-emerald-700 ring-2 ring-emerald-400'
                      : 'bg-white border border-slate-200 text-slate-400 hover:border-emerald-400 hover:bg-emerald-50/40'
                  }`}
                >
                  <span className="text-sm font-black">{slot}</span>
                  <span className="text-[8px] uppercase tracking-wider font-extrabold">
                    {isExplicitOpen ? 'Custom Open' : 'Offline (Closed)'}
                  </span>
                </button>
              );
            } else {
              // Active Day
              if (isStandardShift) {
                return (
                  <button
                    key={slot}
                    onClick={() => toggleDoctorBusySlot(busyDate, slot, 'Doctor marked busy', 'busy')}
                    className={`p-3 rounded-2xl text-xs font-bold transition-all flex flex-col items-center gap-1 shadow-sm ${
                      isExplicitBusy
                        ? 'bg-amber-500 text-white shadow-amber-500/20 hover:bg-amber-600 ring-2 ring-amber-400'
                        : 'bg-white border-2 border-indigo-200 text-slate-800 hover:border-amber-400 hover:bg-amber-50/50'
                    }`}
                  >
                    <span className="text-sm font-black">{slot}</span>
                    <span className="text-[8px] uppercase tracking-wider font-extrabold">
                      {isExplicitBusy ? 'Blocked (Busy)' : 'Active Shift Slot'}
                    </span>
                  </button>
                );
              } else {
                return (
                  <button
                    key={slot}
                    onClick={() => toggleDoctorBusySlot(busyDate, slot, 'Doctor opened slot', 'open')}
                    className={`p-3 rounded-2xl text-xs font-bold transition-all flex flex-col items-center gap-1 shadow-sm ${
                      isExplicitOpen
                        ? 'bg-emerald-600 text-white shadow-emerald-600/20 hover:bg-emerald-700 ring-2 ring-emerald-400'
                        : 'bg-slate-50 border border-slate-200 text-slate-400 hover:border-emerald-400 hover:bg-emerald-50/40'
                    }`}
                  >
                    <span className="text-sm font-black">{slot}</span>
                    <span className="text-[8px] uppercase tracking-wider font-extrabold">
                      {isExplicitOpen ? 'Custom Open' : 'Off-Shift (Click open)'}
                    </span>
                  </button>
                );
              }
            }
          })}
        </div>
      </div>
    </div>
  );
};

export const DoctorDashboard: React.FC<{ onStartConsultation: (id: string) => void }> = ({ onStartConsultation }) => {
    const { user, doctors, allUsers, appointments, allAppointments, medicalRecords, scheduleFollowUp, cancelAppointment, updateUserProfile, adminUpdateAppointment, referPatient, addMedicalRecord, updateMedicalRecord, uploadClinicalFile, uploadPublicFile, resetUserPassword, changeUserPassword, globalSettings, toggleDoctorBusySlot, setDoctorDayBusy, showToast } = useApp();
    const { isCallOngoing, isCallActiveInAnotherTab, isBothConnectedInCall, isCallEnded, callDocStatus } = useActiveCallStatus();
    const [tab, setTab] = useState<'schedule' | 'patients' | 'profile' | 'working' | 'security'>('schedule');
    
    // --- State for Doctor Password & Security ---
    const [docCurrentPass, setDocCurrentPass] = useState('');
    const [docNewPass, setDocNewPass] = useState('');
    const [docConfirmPass, setDocConfirmPass] = useState('');
    const [showDocCurrentPass, setShowDocCurrentPass] = useState(false);
    const [showDocNewPass, setShowDocNewPass] = useState(false);
    const [showDocConfirmPass, setShowDocConfirmPass] = useState(false);
    const [docPassSubmitting, setDocPassSubmitting] = useState(false);
    const [docResetSubmitting, setDocResetSubmitting] = useState(false);
    const [docSecurityFeedback, setDocSecurityFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // --- State for Busy Slots Management ---
    const [busyDate, setBusyDate] = useState(getTodayDateString());
    
    // --- State for Profile ---
    const [editProfile, setEditProfile] = useState({ 
        name: user?.name || '', 
        bio: user?.bio || '', 
        specialties: user?.specialties || [],
        timezone: user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        dob: user?.dob || '',
        gender: user?.gender || '',
        phone: user?.phone || '',
        address: user?.address || '',
        licenseNumber: user?.licenseNumber || user?.medicalLicenseNumber || '',
    });
    const [newSpecialty, setNewSpecialty] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [avatarLoading, setAvatarLoading] = useState(false);

    // --- State for Working Hours ---
    const [allowSpecialRequests, setAllowSpecialRequests] = useState<boolean>(user?.allowSpecialRequests ?? false);
    const [workingHours, setWorkingHours] = useState<WorkingHours>(() => ({
        ...DEFAULT_WORKING_HOURS,
        ...(user?.workingHours || {})
    }));

    useEffect(() => {
        if (user) {
            setAllowSpecialRequests(user.allowSpecialRequests ?? false);
            if (user.workingHours) {
                setWorkingHours({ ...DEFAULT_WORKING_HOURS, ...user.workingHours });
            }
        }
    }, [user?.id, user?.allowSpecialRequests, user?.workingHours]);

    // --- State for Appointment Editing ---
    const [editingApp, setEditingApp] = useState<Appointment | null>(null);

    // --- State for Patient Selection ---
    const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
    const selectedPatient = useMemo(() => allUsers.find(u => u.id === selectedPatientId), [allUsers, selectedPatientId]);

    // --- State for Follow-up Appointment ---
    const [showFollowUpModal, setShowFollowUpModal] = useState(false);
    const [followUpData, setFollowUpData] = useState({ date: '', time: '' });

    // --- State for Referrals ---
    const [referringAppointment, setReferringAppointment] = useState<Appointment | null>(null);
    const [referToDocId, setReferToDocId] = useState('');
    const [referNotes, setReferNotes] = useState('');

    // --- State for On-behalf Record / Post-Consultation Form ---
    const [showRecordModal, setShowRecordModal] = useState(false);
    const [recordPatientId, setRecordPatientId] = useState('');
    const [recordAppointmentId, setRecordAppointmentId] = useState('');
    const [hasRestoredDashboardDraft, setHasRestoredDashboardDraft] = useState(false);
    const [recordData, setRecordData] = useState({
        presentingSymptoms: '',
        history: '',
        pastHistory: '',
        socialHistory: '',
        vitals: '',
        physicalExam: '',
        labReports: '',
        imaging: '',
        diagnosis: '',
        treatmentPlan: '',
        prescription: '',
        followUp: '',
        referrals: '',
        notes: ''
    });
    const [recordFiles, setRecordFiles] = useState<File[]>([]);
    const [triageAppointment, setTriageAppointment] = useState<Appointment | null>(null);
    const [viewingPrescriptionRecord, setViewingPrescriptionRecord] = useState<MedicalRecord | null>(null);
    const [viewingMetadataRecord, setViewingMetadataRecord] = useState<MedicalRecord | null>(null);

    // --- State for Editing/Amending Existing Medical Records (with Audit History) ---
    const [amendingRecord, setAmendingRecord] = useState<MedicalRecord | null>(null);
    const [amendFormData, setAmendFormData] = useState({
        diagnosis: '',
        prescription: '',
        treatmentPlan: '',
        followUpInstructions: '',
        notes: '',
        presentingSymptoms: '',
        historyOfPresentIllness: '',
        vitalSigns: '',
        physicalExamFindings: '',
        referralsIssued: ''
    });
    const [amendReason, setAmendReason] = useState('');
    const [isSavingAmendment, setIsSavingAmendment] = useState(false);
    const [expandedAuditRecordId, setExpandedAuditRecordId] = useState<string | null>(null);

    const openAmendRecordModal = (record: MedicalRecord) => {
        setAmendingRecord(record);
        setAmendFormData({
            diagnosis: record.diagnosis || '',
            prescription: record.prescription || '',
            treatmentPlan: record.treatmentPlan || '',
            followUpInstructions: record.followUpInstructions || '',
            notes: record.notes || '',
            presentingSymptoms: record.presentingSymptoms || '',
            historyOfPresentIllness: record.historyOfPresentIllness || '',
            vitalSigns: record.vitalSigns || '',
            physicalExamFindings: record.physicalExamFindings || '',
            referralsIssued: record.referralsIssued || ''
        });
        setAmendReason('');
    };

    const handleSaveRecordAmendment = async () => {
        if (!amendingRecord) return;
        if (!amendReason.trim()) {
            showToast("Note Required", "Please add a quick note describing the update.", "error");
            return;
        }
        setIsSavingAmendment(true);
        try {
            await updateMedicalRecord(
                amendingRecord.id,
                {
                    diagnosis: amendFormData.diagnosis,
                    prescription: amendFormData.prescription,
                    treatmentPlan: amendFormData.treatmentPlan,
                    followUpInstructions: amendFormData.followUpInstructions,
                    notes: amendFormData.notes,
                    presentingSymptoms: amendFormData.presentingSymptoms,
                    historyOfPresentIllness: amendFormData.historyOfPresentIllness,
                    vitalSigns: amendFormData.vitalSigns,
                    physicalExamFindings: amendFormData.physicalExamFindings,
                    referralsIssued: amendFormData.referralsIssued
                },
                amendReason.trim()
            );
            showToast("Notes Updated", "Visit notes updated successfully!", "success");
            setAmendingRecord(null);
        } catch (err: any) {
            console.error("Error updating medical record:", err);
            showToast("Update Failed", "Could not update notes: " + (err.message || "Please try again"), "error");
        } finally {
            setIsSavingAmendment(false);
        }
    };

    // Auto-save post-consultation draft in DoctorDashboard
    useEffect(() => {
        if (!showRecordModal || typeof window === 'undefined') return;
        const targetId = recordAppointmentId || recordPatientId;
        if (!targetId) return;

        const hasContent = Object.values(recordData).some(val => typeof val === 'string' && val.trim().length > 0);
        if (!hasContent) return;

        const draftKey = `rovita_doctor_modal_draft_${targetId}`;
        const timeout = setTimeout(() => {
            try {
                localStorage.setItem(draftKey, safeStringify(recordData));
            } catch (e) {}
        }, 500);

        return () => clearTimeout(timeout);
    }, [recordData, showRecordModal, recordAppointmentId, recordPatientId]);

    const openPostConsultationModal = (patientId: string, appointmentId?: string, prefill?: Partial<typeof recordData>) => {
        setRecordPatientId(patientId);
        setRecordAppointmentId(appointmentId || '');
        setHasRestoredDashboardDraft(false);

        let initialData = {
            presentingSymptoms: prefill?.presentingSymptoms || '',
            history: prefill?.history || '',
            pastHistory: prefill?.pastHistory || '',
            socialHistory: prefill?.socialHistory || '',
            vitals: prefill?.vitals || '',
            physicalExam: prefill?.physicalExam || '',
            labReports: prefill?.labReports || '',
            imaging: prefill?.imaging || '',
            diagnosis: prefill?.diagnosis || '',
            treatmentPlan: prefill?.treatmentPlan || '',
            prescription: prefill?.prescription || '',
            followUp: prefill?.followUp || '',
            referrals: prefill?.referrals || '',
            notes: prefill?.notes || ''
        };

        const targetId = appointmentId || patientId;
        if (targetId && typeof window !== 'undefined') {
            try {
                const draftKey = `rovita_doctor_modal_draft_${targetId}`;
                const saved = localStorage.getItem(draftKey);
                if (saved) {
                    const parsed = JSON.parse(saved);
                    if (parsed && typeof parsed === 'object') {
                        initialData = { ...initialData, ...parsed };
                        setHasRestoredDashboardDraft(true);
                    }
                }
            } catch (e) {}
        }

        setRecordData(initialData);
        setRecordFiles([]);
        setShowRecordModal(true);
    };

    const commonSpecialties = ['General Physician', 'Nephrology', 'Cardiology', 'Neurology', 'Dentist', 'Pediatrics', 'Mental Health', 'Oncology', 'Dermatology', 'Orthopedics', 'Urology', 'Gastroenterology'];

    // Memoized filters
    const upcoming = useMemo(() => {
        return appointments
            .filter(a => a.status === 'upcoming' || a.status === 'in-progress')
            .sort((a, b) => new Date(`${a.date} ${a.time}`).getTime() - new Date(`${b.date} ${b.time}`).getTime());
    }, [appointments]);

    const past = useMemo(() => {
        return appointments
            .filter(a => a.status !== 'upcoming')
            .sort((a, b) => new Date(`${b.date} ${b.time}`).getTime() - new Date(`${a.date} ${a.time}`).getTime());
    }, [appointments]);

    const associatedPatients = useMemo(() => {
        const unique = new Set<string>();
        const list: { id: string; name: string; mrn?: string }[] = [];
        appointments.forEach(a => {
            if (!unique.has(a.patientId)) {
                unique.add(a.patientId);
                const fullProfile = allUsers.find(u => u.id === a.patientId);
                list.push({ id: a.patientId, name: a.patientName, mrn: fullProfile?.mrn });
            }
        });
        return list;
    }, [appointments, allUsers]);

    const patientRecords = useMemo(() => {
        if (!selectedPatientId) return [];
        return medicalRecords.filter(r => r.patientId === selectedPatientId).sort((a, b) => parseRecordTimestamp(b) - parseRecordTimestamp(a));
    }, [medicalRecords, selectedPatientId]);

    // --- Search & Filter States for Doctor Web Dashboard ---
    const [appointmentSearch, setAppointmentSearch] = useState('');
    const [appointmentStatusFilter, setAppointmentStatusFilter] = useState<'all' | 'upcoming' | 'in-progress' | 'completed'>('all');
    const [patientSearch, setPatientSearch] = useState('');

    const filteredUpcoming = useMemo(() => {
        return upcoming.filter(a => {
            const matchesSearch = !appointmentSearch.trim() || 
                a.patientName.toLowerCase().includes(appointmentSearch.toLowerCase()) ||
                a.date.includes(appointmentSearch) ||
                (a.triage?.chiefComplaint && a.triage.chiefComplaint.toLowerCase().includes(appointmentSearch.toLowerCase()));
            const matchesFilter = appointmentStatusFilter === 'all' || 
                (appointmentStatusFilter === 'upcoming' && a.status !== 'in-progress') ||
                (appointmentStatusFilter === 'in-progress' && a.status === 'in-progress');
            return matchesSearch && matchesFilter;
        });
    }, [upcoming, appointmentSearch, appointmentStatusFilter]);

    const filteredPast = useMemo(() => {
        return past.filter(a => {
            const matchesSearch = !appointmentSearch.trim() || 
                a.patientName.toLowerCase().includes(appointmentSearch.toLowerCase()) ||
                a.date.includes(appointmentSearch);
            const matchesFilter = appointmentStatusFilter === 'all' || appointmentStatusFilter === 'completed';
            return matchesSearch && matchesFilter;
        });
    }, [past, appointmentSearch, appointmentStatusFilter]);

    const filteredPatients = useMemo(() => {
        return associatedPatients.filter(p => {
            if (!patientSearch.trim()) return true;
            const q = patientSearch.toLowerCase();
            return p.name.toLowerCase().includes(q) || 
                (p.mrn && p.mrn.toLowerCase().includes(q)) ||
                (p.id && p.id.toLowerCase().includes(q));
        });
    }, [associatedPatients, patientSearch]);

    // Ensure state updates if user object refreshes
    useEffect(() => {
        if (user) {
            setEditProfile(prev => ({
                ...prev,
                name: user.name,
                bio: user.bio || '',
                specialties: user.specialties || [],
                timezone: user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                dob: user.dob || '',
                phone: user.phone || '',
                gender: user.gender || '',
                address: user.address || '',
                licenseNumber: user.licenseNumber || user.medicalLicenseNumber || ''
            }));
        }
    }, [user]);

    if (!user) return null;

    const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setAvatarLoading(true);
            try {
                const result = await uploadPublicFile(file);
                await updateUserProfile({ avatar: result.url });
            } catch (err: any) { 
                showToast("Upload Failed", "Failed to upload photo. Please check your connection.", "error"); 
                console.error(err);
            }
            setAvatarLoading(false);
            e.target.value = '';
        }
    };

    const handleCredentialUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]) {
            setIsSaving(true);
            try {
                const result = await uploadClinicalFile(e.target.files[0]);
                await updateUserProfile({ credentialUrl: result.url });
                showToast("Documents Uploaded", "Your documents have been uploaded! Our team will review them shortly.", "success");
            } catch (err: any) { 
                showToast("Upload Failed", "We couldn't upload your documents.", "error"); 
            }
            setIsSaving(false);
        }
    };

    const addSpecialty = (s?: string) => {
        const specialtyToAdd = s || newSpecialty;
        if (specialtyToAdd && !editProfile.specialties.includes(specialtyToAdd)) {
            setEditProfile({...editProfile, specialties: [...editProfile.specialties, specialtyToAdd]});
            if (!s) setNewSpecialty('');
        }
    };

    const removeSpecialty = (s: string) => {
        setEditProfile({...editProfile, specialties: editProfile.specialties.filter(x => x !== s)});
    };

    const saveProfileChanges = async () => {
        setIsSaving(true);
        const humaneRovitaId = user?.rovitaLicenseId || formatHumaneRovitaLicenseId(user);
        await updateUserProfile({
            ...editProfile,
            medicalLicenseNumber: editProfile.licenseNumber,
            licenseNumber: editProfile.licenseNumber,
            rovitaLicenseId: humaneRovitaId
        });
        setIsSaving(false);
        showToast("Profile Updated", "Your profile and medical license credentials have been updated!", "success");
    };

    const saveWorkingHours = async () => {
        setIsSaving(true);
        try {
            await updateUserProfile({ workingHours, allowSpecialRequests });
            showToast("Schedule Saved", "Your availability schedule and special request preferences have been updated successfully.", "success");
        } catch (e) {
            showToast("Save Failed", "We couldn't save your schedule.", "error");
        }
        setIsSaving(false);
    };

    const confirmReferral = async () => {
        if (!referringAppointment || !referToDocId) return;
        
        const targetDoc = doctors.find(d => d.id === referToDocId);
        if (!targetDoc) return;

        const isBooked = allAppointments.some(a => 
            a.doctorId === referToDocId && 
            a.date === referringAppointment.date && 
            a.time === referringAppointment.time &&
            a.status === 'upcoming'
        );

        if (isBooked) {
            const targetPrefixed = targetDoc.name.toLowerCase().startsWith('dr') ? targetDoc.name : `Dr. ${targetDoc.name}`;
            showToast("Doctor Unavailable", `${targetPrefixed} is already booked at ${referringAppointment.time} on ${referringAppointment.date}. Please reschedule first or choose another doctor.`, "error");
            return;
        }

        try {
            await referPatient(referringAppointment.patientId, referToDocId, referNotes);
            const docPrefixed = user.name.toLowerCase().startsWith('dr') ? user.name : `Dr. ${user.name}`;
            await adminUpdateAppointment(referringAppointment.id, {
                doctorId: targetDoc.id,
                doctorName: targetDoc.name,
                notes: `${referringAppointment.notes || ''}\n\n[TRANSFERRED] Referred from ${docPrefixed} on ${new Date().toLocaleDateString()}. Notes: ${referNotes}`
            });

            setReferringAppointment(null);
            setReferToDocId('');
            setReferNotes('');
            const targetPrefixed = targetDoc.name.toLowerCase().startsWith('dr') ? targetDoc.name : `Dr. ${targetDoc.name}`;
            showToast("Patient Transferred", `Patient successfully transferred to ${targetPrefixed}. The appointment has been moved to their schedule.`, "success");
        } catch (e: any) {
            showToast("Transfer Failed", e.message || "Failed to transfer appointment.", "error");
        }
    };

    const submitOnBehalfRecord = async () => {
        if (!recordPatientId) { showToast("Patient Required", "Please select a patient.", "error"); return; }
        if (!recordData.diagnosis.trim()) { showToast("Diagnosis Required", "Please provide a clinical diagnosis/assessment.", "error"); return; }
        setIsSaving(true);
        try {
            let uploadedFiles: { name: string; url: string; size?: number; type?: string }[] = [];
            if (recordFiles.length > 0) {
                const results = await Promise.all(recordFiles.map(f => uploadClinicalFile(f)));
                uploadedFiles = results.map((r, i) => ({
                    name: r.name,
                    url: r.url,
                    size: recordFiles[i]?.size,
                    type: recordFiles[i]?.type
                }));
            }
            
            const randomRxId = recordData.prescription?.trim() ? Math.floor(100000 + Math.random() * 900000).toString() : undefined;
            const docPrefixed = user.name.toLowerCase().startsWith('dr') ? user.name : `Dr. ${user.name}`;

            await addMedicalRecord({
                patientId: recordPatientId,
                doctorId: user.id,
                doctorName: docPrefixed,
                date: getTodayDateString(),
                presentingSymptoms: recordData.presentingSymptoms,
                historyOfPresentIllness: recordData.history,
                pastMedicalHistory: recordData.pastHistory,
                socialHistory: recordData.socialHistory,
                vitalSigns: recordData.vitals,
                physicalExamFindings: recordData.physicalExam,
                labReports: recordData.labReports,
                imagingStudies: recordData.imaging,
                diagnosis: recordData.diagnosis,
                treatmentPlan: recordData.treatmentPlan,
                prescription: recordData.prescription,
                rxId: randomRxId,
                followUpInstructions: recordData.followUp,
                referralsIssued: recordData.referrals,
                notes: recordData.notes,
                appointmentId: recordAppointmentId || undefined,
                attachmentUrl: uploadedFiles[0]?.url || '',
                attachmentName: uploadedFiles[0]?.name || '',
                attachments: uploadedFiles,
                authoredBy: 'doctor'
            });

            if (recordAppointmentId) {
                try {
                    await adminUpdateAppointment(recordAppointmentId, { status: 'completed' });
                } catch (err) {
                    console.error("Failed to mark appointment completed:", err);
                }
            }

            if (typeof window !== 'undefined') {
                const targetId = recordAppointmentId || recordPatientId;
                if (targetId) {
                    try {
                        localStorage.removeItem(`rovita_doctor_modal_draft_${targetId}`);
                    } catch (e) {}
                }
            }

            setShowRecordModal(false);
            setRecordData({
                presentingSymptoms: '',
                history: '',
                pastHistory: '',
                socialHistory: '',
                vitals: '',
                physicalExam: '',
                labReports: '',
                imaging: '',
                diagnosis: '',
                treatmentPlan: '',
                prescription: '',
                followUp: '',
                referrals: '',
                notes: ''
            });
            setRecordAppointmentId('');
            setRecordFiles([]);
            showToast("Record Saved", "Visit summary and prescription have been saved to the patient's records!", "success");
        } catch (e: any) { 
            console.error("Failed to save clinical record:", e);
            showToast("Save Failed", e.message || "Could not save the record. Please try again.", "error"); 
        }
        setIsSaving(false);
    };

    const handleScheduleFollowUp = async () => {
        if (!selectedPatientId || !followUpData.date || !followUpData.time) {
            showToast("Incomplete Details", "Please provide date and time for the follow-up.", "error");
            return;
        }

        setIsSaving(true);
        try {
            await scheduleFollowUp(selectedPatientId, user.id, followUpData.date, followUpData.time, user.timezone || 'UTC');
            setShowFollowUpModal(false);
            setFollowUpData({ date: '', time: '' });
            showToast("Follow-Up Scheduled", "Complimentary follow-up appointment successfully scheduled!", "success");
        } catch (e: any) {
            showToast("Schedule Failed", e.message || "Failed to schedule follow-up.", "error");
        }
        setIsSaving(false);
    };

    // Body scroll lock effect
    useEffect(() => {
        if (editingApp || referringAppointment || showRecordModal || showFollowUpModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        return () => { document.body.style.overflow = 'auto'; };
    }, [editingApp, referringAppointment, showRecordModal, showFollowUpModal]);

    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    return (
        <div className="space-y-8 animate-in fade-in duration-500 font-['Plus_Jakarta_Sans']">
            {/* Real-time Call Alert */}
            <LiveCallNotificationBanner onJoinRoom={onStartConsultation} />

            {/* Top Doctor Profile & Web Navigation Header */}
            <div className="bg-white p-6 md:p-8 rounded-[2.5rem] md:rounded-[3rem] border border-slate-100 shadow-sm flex flex-col xl:flex-row justify-between items-start xl:items-center gap-6">
                <div className="flex items-center gap-5 md:gap-6 min-w-0">
                    <div className="relative group shrink-0">
                        <img src={user.avatar} className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-slate-100 border-4 border-white shadow-md object-cover" alt="Doctor" />
                        <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-2xl opacity-0 group-hover:opacity-100 cursor-pointer transition-all z-10">
                            {avatarLoading ? (
                                <i className="fa-solid fa-circle-notch fa-spin text-white text-lg"></i>
                            ) : (
                                <i className="fa-solid fa-camera text-white text-lg"></i>
                            )}
                            <input type="file" className="hidden" onChange={handleAvatarChange} accept="image/*" disabled={avatarLoading} />
                        </label>
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight truncate">
                                {user.name.toLowerCase().startsWith('dr') ? user.name : `Dr. ${user.name}`}
                            </h2>
                            <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase text-emerald-700 bg-emerald-50 border border-emerald-200/80 px-2 py-0.5 rounded-lg">
                                <i className="fa-solid fa-circle-check text-emerald-500"></i> Verified
                            </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                            {user.specialties?.length ? user.specialties.map(s => (
                                <span key={s} className="text-indigo-700 font-black text-[11px] uppercase tracking-wider bg-indigo-50/90 border border-indigo-100/60 px-2.5 py-0.5 rounded-lg">
                                    {s}
                                </span>
                            )) : (
                                <span className="text-slate-500 font-bold text-xs bg-slate-100 px-2.5 py-0.5 rounded-lg">General Physician</span>
                            )}
                            <span className="text-[11px] font-bold text-slate-400 ml-1">
                                • {user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Tab Navigation Menu */}
                <div className="flex flex-wrap sm:flex-nowrap overflow-x-auto gap-1.5 w-full xl:w-auto p-1.5 bg-slate-100/80 rounded-2xl border border-slate-200/60 scrollbar-hide">
                    {[
                      { id: 'schedule', label: 'Appointments', icon: 'fa-calendar-check', count: upcoming.length },
                      { id: 'patients', label: 'Patient Records', icon: 'fa-user-group', count: associatedPatients.length },
                      { id: 'working', label: 'Office Hours', icon: 'fa-clock' },
                      { id: 'profile', label: 'My Profile', icon: 'fa-id-card' },
                      { id: 'security', label: 'Security', icon: 'fa-shield-halved' }
                    ].map(t => (
                        <button 
                            key={t.id} 
                            onClick={() => setTab(t.id as any)} 
                            className={`flex-1 sm:flex-initial shrink-0 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all whitespace-nowrap flex items-center justify-center gap-2 ${
                                tab === t.id 
                                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/20' 
                                    : 'text-slate-600 hover:text-indigo-600 hover:bg-white/60'
                            }`}
                        >
                            <i className={`fa-solid ${t.icon} text-xs`}></i> 
                            <span>{t.label}</span>
                            {t.count !== undefined && t.count > 0 && (
                                <span className={`text-[10px] font-black px-1.5 py-0.2 rounded-full ${
                                    tab === t.id ? 'bg-white/20 text-white' : 'bg-indigo-100 text-indigo-700'
                                }`}>
                                    {t.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Doctor Overview KPI Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-xs flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-xl shrink-0">
                        <i className="fa-solid fa-calendar-days"></i>
                    </div>
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Scheduled Visits</p>
                        <p className="text-2xl font-black text-slate-900">{upcoming.length}</p>
                    </div>
                </div>

                <div className={`p-5 rounded-2xl border shadow-xs flex items-center gap-4 transition-all ${
                    upcoming.some(a => a.status === 'in-progress' && !isCallEnded(a.id))
                        ? 'bg-rose-50/80 border-rose-200'
                        : 'bg-white border-slate-100'
                }`}>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl shrink-0 ${
                        upcoming.some(a => a.status === 'in-progress' && !isCallEnded(a.id))
                            ? 'bg-rose-500 text-white animate-pulse'
                            : 'bg-emerald-50 text-emerald-600'
                    }`}>
                        <i className="fa-solid fa-video"></i>
                    </div>
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Live Active Rooms</p>
                        <p className="text-2xl font-black text-slate-900">
                            {upcoming.filter(a => a.status === 'in-progress' && !isCallEnded(a.id)).length}
                        </p>
                    </div>
                </div>

                <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-xs flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-xl shrink-0">
                        <i className="fa-solid fa-users"></i>
                    </div>
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Total Patients In Care</p>
                        <p className="text-2xl font-black text-slate-900">{associatedPatients.length}</p>
                    </div>
                </div>

                <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-xs flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center text-xl shrink-0">
                        <i className="fa-solid fa-user-clock"></i>
                    </div>
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Office Availability</p>
                        <p className="text-sm font-black text-emerald-600 flex items-center gap-1 mt-0.5">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping"></span>
                            Accepting Calls
                        </p>
                    </div>
                </div>
            </div>

            {/* Email Caution Banner */}
            <div className="bg-teal-50/70 border border-teal-100/90 rounded-2xl p-4 md:p-5 flex items-start gap-4">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-teal-600 shrink-0 shadow-xs">
                    <i className="fa-solid fa-envelope-open-text"></i>
                </div>
                <div className="flex-grow">
                    <p className="text-xs md:text-sm font-black text-teal-950 mb-0.5">Automated Notifications & Reminders</p>
                    <p className="text-teal-800 text-[10px] md:text-xs font-medium">
                        Patient confirmations, follow-up alerts, and clinical reminders are dispatched automatically to patient emails and in-app portals.
                    </p>
                </div>
            </div>

            {/* Content Area */}
            <div className="min-h-[500px]">
                {tab === 'schedule' && (
                    <div className="space-y-6">
                        {/* Search & Filter Toolbar for Appointments */}
                        <div className="bg-white p-4 md:p-5 rounded-2xl border border-slate-100 shadow-xs flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
                            <div className="relative flex-1 max-w-md">
                                <i className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs"></i>
                                <input 
                                    type="text"
                                    value={appointmentSearch}
                                    onChange={(e) => setAppointmentSearch(e.target.value)}
                                    placeholder="Search by patient, triage, date..."
                                    className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:bg-white focus:border-indigo-500 focus:outline-none transition-all"
                                />
                                {appointmentSearch && (
                                    <button onClick={() => setAppointmentSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs">
                                        <i className="fa-solid fa-xmark"></i>
                                    </button>
                                )}
                            </div>

                            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0 scrollbar-hide">
                                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 mr-1 hidden sm:inline">Filter:</span>
                                {[
                                    { id: 'all', label: 'All', count: upcoming.length + past.length },
                                    { id: 'upcoming', label: 'Upcoming', count: upcoming.filter(a => a.status !== 'in-progress').length },
                                    { id: 'in-progress', label: 'Live Active', count: upcoming.filter(a => a.status === 'in-progress').length },
                                    { id: 'completed', label: 'History', count: past.length }
                                ].map(f => (
                                    <button
                                        key={f.id}
                                        onClick={() => setAppointmentStatusFilter(f.id as any)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1.5 ${
                                            appointmentStatusFilter === f.id
                                                ? 'bg-slate-900 text-white shadow-xs'
                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                        }`}
                                    >
                                        <span>{f.label}</span>
                                        <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                                            appointmentStatusFilter === f.id ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'
                                        }`}>{f.count}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Web Grid Layout: Left Column (Upcoming Visits) & Right Column (History) */}
                        <div className="grid lg:grid-cols-12 gap-8 items-start">
                            {/* Left Column: Upcoming & Live Consultations */}
                            <div className={`${appointmentStatusFilter === 'completed' ? 'hidden' : appointmentStatusFilter === 'upcoming' || appointmentStatusFilter === 'in-progress' ? 'lg:col-span-12' : 'lg:col-span-7'} space-y-6`}>
                                <div className="flex items-center justify-between">
                                    <SectionTitle title="Upcoming Consultations" subtitle="Patients queued for clinical tele-visits today." />
                                    <span className="text-xs font-black text-indigo-600 bg-indigo-50 px-3 py-1 rounded-xl">
                                        {filteredUpcoming.length} {filteredUpcoming.length === 1 ? 'Visit' : 'Visits'}
                                    </span>
                                </div>

                                {filteredUpcoming.length === 0 ? (
                                    <div className="p-12 text-center bg-white rounded-3xl border border-slate-100 shadow-xs">
                                        <div className="w-14 h-14 bg-slate-50 text-slate-300 rounded-2xl flex items-center justify-center mx-auto mb-3 text-xl">
                                            <i className="fa-solid fa-calendar-check"></i>
                                        </div>
                                        <p className="text-slate-500 font-bold text-sm">No scheduled consultations match your filter.</p>
                                        <p className="text-slate-400 text-xs mt-1">Upcoming appointments booked by patients will appear here in real-time.</p>
                                    </div>
                                ) : (
                                    filteredUpcoming.map(a => (
                                        <div 
                                            key={a.id} 
                                            className={`p-6 md:p-7 rounded-3xl border shadow-xs flex flex-col gap-5 transition-all hover:shadow-md ${
                                                a.status === 'in-progress' 
                                                    ? 'bg-rose-50/40 border-rose-200 ring-2 ring-rose-400/40' 
                                                    : 'bg-white border-slate-100'
                                            }`}
                                        >
                                            {a.status === 'in-progress' && (
                                                <div className="flex items-center justify-between px-4 py-2 bg-rose-600 text-white rounded-xl text-xs font-black uppercase tracking-wider animate-pulse shadow-sm">
                                                    <span className="flex items-center gap-2">
                                                        <span className="w-2.5 h-2.5 rounded-full bg-white animate-ping"></span>
                                                        🔴 Live Consultation In Progress (Video Room Active)
                                                    </span>
                                                    <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-md">Connected</span>
                                                </div>
                                            )}

                                            <div className="flex justify-between items-start gap-4">
                                                <div className="flex items-center gap-4 min-w-0">
                                                    <div className={`w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center text-lg md:text-xl font-black shrink-0 ${
                                                        a.status === 'in-progress' ? 'bg-rose-100 text-rose-600' : 'bg-indigo-50 text-indigo-600'
                                                    }`}>
                                                        {a.patientName[0]}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <h4 className="text-lg md:text-xl font-black text-slate-900 truncate">{a.patientName}</h4>
                                                            {(() => {
                                                                const patientObj = allUsers.find(u => u.id === a.patientId) || null;
                                                                return (
                                                                    <ParticipantStatusBadge user={patientObj} callStatus={callDocStatus[a.id]} isDoctorViewing={true} />
                                                                );
                                                            })()}
                                                            {a.triage && (
                                                                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-md border ${
                                                                    (a.triage.severity || 1) >= 7 ? 'bg-rose-100 text-rose-700 border-rose-200' :
                                                                    (a.triage.severity || 1) >= 4 ? 'bg-amber-100 text-amber-700 border-amber-200' :
                                                                    'bg-emerald-100 text-emerald-700 border-emerald-200'
                                                                }`}>
                                                                    Triage: {a.triage.severity}/10
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mt-0.5">
                                                            <i className="fa-regular fa-clock mr-1 text-slate-400"></i>
                                                            {a.date} • {a.time}
                                                        </p>
                                                        <p className="text-[11px] font-medium text-slate-400">Doctor TZ: {user.timezone || 'UTC'}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1.5 shrink-0">
                                                    <button 
                                                        onClick={() => setEditingApp(a)} 
                                                        className="w-9 h-9 bg-slate-50 text-slate-500 rounded-xl hover:bg-indigo-600 hover:text-white transition-all flex items-center justify-center text-xs" 
                                                        title="Change Time/Status"
                                                    >
                                                        <i className="fa-solid fa-pen-to-square"></i>
                                                    </button>
                                                    <button 
                                                        onClick={() => cancelAppointment(a.id, "Doctor cancelled via dashboard")} 
                                                        className="w-9 h-9 bg-rose-50 text-rose-500 rounded-xl hover:bg-rose-500 hover:text-white transition-all flex items-center justify-center text-xs" 
                                                        title="Cancel Visit"
                                                    >
                                                        <i className="fa-solid fa-xmark"></i>
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Pre-Consultation Triage Box if Available */}
                                            {a.triage ? (
                                                <div className="p-4 bg-indigo-50/60 border border-indigo-100 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                                    <div className="space-y-0.5 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[11px] font-black uppercase text-indigo-700 tracking-wider">
                                                                <i className="fa-solid fa-clipboard-check mr-1 text-emerald-600"></i>
                                                                Pre-Consultation Triage Available
                                                            </span>
                                                        </div>
                                                        <p className="text-xs font-bold text-slate-700 truncate">
                                                            Complaint: <span className="font-normal text-slate-600">{a.triage.chiefComplaint}</span>
                                                        </p>
                                                        {a.triage.knownAllergies && a.triage.knownAllergies !== 'None' && (
                                                            <p className="text-[11px] font-bold text-rose-600">
                                                                ⚠️ Allergies: {a.triage.knownAllergies}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <button 
                                                        onClick={() => setTriageAppointment(a)}
                                                        className="shrink-0 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-xs"
                                                    >
                                                        <i className="fa-solid fa-file-medical"></i> Review Triage
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 flex items-center justify-between text-xs text-slate-500">
                                                    <span><i className="fa-regular fa-clipboard mr-1.5 text-slate-400"></i> Pre-consultation triage pending from patient</span>
                                                </div>
                                            )}

                                            {/* Confidential Doctor Notes Saved Indicator */}
                                            {a.privateNotes && (
                                                <div className="p-3 bg-amber-50/70 border border-amber-200/80 rounded-xl text-xs text-amber-900 space-y-1">
                                                    <p className="font-black uppercase tracking-wider text-[10px] text-amber-700 flex items-center gap-1.5">
                                                        <i className="fa-solid fa-lock text-amber-600"></i> Doctor Private Notes
                                                    </p>
                                                    <p className="line-clamp-2 font-mono text-[11px] text-slate-700">{a.privateNotes}</p>
                                                </div>
                                            )}

                                            {/* Responsive Action Buttons */}
                                            <div className="flex flex-wrap items-center gap-2.5 pt-1">
                                                {(() => {
                                                    const isCallEndedForApt = isCallEnded(a.id);
                                                    const isAptCallActive = !isCallEndedForApt && (isCallOngoing(a.id) || (a.status === 'in-progress' && isCallActiveInAnotherTab()));
                                                    if (isAptCallActive) {
                                                        return (
                                                            <div className="flex-1 min-w-[200px] flex flex-col gap-1">
                                                                <div className="py-3 px-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-2xl text-xs font-black flex items-center justify-center gap-2">
                                                                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping"></span>
                                                                    <i className="fa-solid fa-phone-volume text-emerald-600"></i>
                                                                    <span>Consultation In Progress</span>
                                                                </div>
                                                                <button
                                                                    onClick={() => onStartConsultation(a.id)}
                                                                    className="text-[11px] font-bold text-slate-400 hover:text-indigo-600 underline text-center transition-colors"
                                                                    title="Switch ongoing consultation to this window"
                                                                >
                                                                    Switch call to this tab
                                                                </button>
                                                            </div>
                                                        );
                                                    }
                                                    const patientObj = allUsers.find(u => u.id === a.patientId) || null;
                                                    const patStatus = getParticipantOnlineStatus(patientObj, callDocStatus[a.id], true);
                                                    const isLiveActive = a.status === 'in-progress' && !isCallEndedForApt;
                                                    return (
                                                        <Button 
                                                            onClick={() => onStartConsultation(a.id)} 
                                                            className={`flex-1 min-w-[200px] py-3 text-xs font-black shadow-md ${
                                                                isLiveActive 
                                                                    ? 'bg-rose-600 hover:bg-rose-700 shadow-rose-500/30' 
                                                                    : patStatus.isInRoom
                                                                        ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/30 ring-2 ring-emerald-400/50'
                                                                        : 'shadow-indigo-500/20'
                                                            }`}
                                                        >
                                                            {isLiveActive 
                                                                ? '🔴 Rejoin Live Video Room' 
                                                                : patStatus.isInRoom
                                                                    ? '🟢 Patient in Room • Launch Call'
                                                                    : patStatus.isOnline
                                                                        ? '🟢 Patient Online • Launch Call'
                                                                        : 'Launch Consultation'}
                                                        </Button>
                                                    );
                                                })()}
                                                <button 
                                                    onClick={() => openPostConsultationModal(a.patientId, a.id, { presentingSymptoms: a.triage?.chiefComplaint || a.notes || '', notes: a.privateNotes || '' })} 
                                                    className="px-4 py-3 bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white rounded-2xl transition-all font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-xs shrink-0" 
                                                    title="Open and submit official clinical post-consultation form"
                                                >
                                                    <i className="fa-solid fa-file-medical"></i> Post-Call Rx
                                                </button>
                                                <button 
                                                    onClick={() => setReferringAppointment(a)} 
                                                    className="px-4 py-3 bg-amber-50 text-amber-700 hover:bg-amber-500 hover:text-white rounded-2xl transition-all font-black text-xs uppercase tracking-wider shrink-0" 
                                                    title="Transfer care to another specialist doctor"
                                                >
                                                    <i className="fa-solid fa-share-nodes mr-1.5"></i> Refer Patient
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            {/* Right Column: Previous Consultation History */}
                            <div className={`${appointmentStatusFilter === 'upcoming' || appointmentStatusFilter === 'in-progress' ? 'hidden' : appointmentStatusFilter === 'completed' ? 'lg:col-span-12' : 'lg:col-span-5'} space-y-6`}>
                                <div className="flex items-center justify-between">
                                    <SectionTitle title="Completed History" subtitle="Archived consultations & past reports." />
                                    <span className="text-xs font-black text-slate-500 bg-slate-100 px-3 py-1 rounded-xl">
                                        {filteredPast.length} Total
                                    </span>
                                </div>

                                <div className="space-y-3.5 max-h-[750px] overflow-y-auto pr-1 scrollbar-hide">
                                    {filteredPast.map(a => (
                                        <div key={a.id} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 hover:border-indigo-100 hover:shadow-sm transition-all">
                                            <div className="flex items-center gap-3.5 min-w-0">
                                                <div className="w-10 h-10 bg-slate-100 text-slate-600 rounded-xl flex items-center justify-center text-sm font-bold shrink-0">
                                                    {a.patientName[0]}
                                                </div>
                                                <div className="min-w-0">
                                                    <h4 className="font-black text-slate-900 text-sm truncate">{a.patientName}</h4>
                                                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{a.date}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end shrink-0">
                                                <button 
                                                    onClick={() => openPostConsultationModal(a.patientId, a.id, { presentingSymptoms: a.triage?.chiefComplaint || a.notes || '', notes: a.privateNotes || '' })}
                                                    className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-600 hover:text-white text-indigo-700 rounded-xl text-[11px] font-bold transition-all flex items-center gap-1.5 shadow-2xs"
                                                    title="View or update clinical post-consultation form"
                                                >
                                                    <i className="fa-solid fa-file-medical text-[10px]"></i>
                                                    <span>Clinical Form</span>
                                                </button>
                                                <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${
                                                    a.status === 'completed' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-rose-50 text-rose-500 border border-rose-100'
                                                }`}>
                                                    {a.status}
                                                </span>
                                                <button onClick={() => setEditingApp(a)} className="w-7 h-7 rounded-lg text-slate-300 hover:text-indigo-600 transition-colors flex items-center justify-center">
                                                    <i className="fa-solid fa-ellipsis-vertical text-xs"></i>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    {filteredPast.length === 0 && (
                                        <div className="p-8 text-center bg-white rounded-2xl border border-slate-100">
                                            <p className="text-slate-400 text-xs italic">No past appointments recorded.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {tab === 'patients' && (
                   <div className="bg-white p-6 md:p-10 rounded-[2.5rem] md:rounded-[3rem] border border-slate-100 shadow-sm space-y-6">
                       <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-slate-100">
                           <SectionTitle 
                                title={selectedPatientId ? `Clinical Record: ${selectedPatient?.name || 'Patient'}` : "Patient Directory & Records"} 
                                subtitle={selectedPatientId ? (selectedPatient?.mrn ? `MRN: ${selectedPatient.mrn}` : `ID: ${selectedPatientId.slice(0,8)}`) : "View longitudinal health timelines, prescription audits, and consult summaries."} 
                           />
                           <div className="flex gap-2 w-full sm:w-auto">
                               {selectedPatientId && (
                                   <Button onClick={() => setSelectedPatientId(null)} variant="ghost" className="flex-1 sm:flex-none">
                                       <i className="fa-solid fa-arrow-left mr-2"></i> Back to Directory
                                   </Button>
                               )}
                               <Button onClick={() => {
                                   openPostConsultationModal(selectedPatientId || '');
                               }} variant="secondary" className="flex-1 sm:flex-none">
                                   <i className="fa-solid fa-file-medical mr-2"></i> Create Clinical Record
                               </Button>
                           </div>
                       </div>

                       {!selectedPatientId ? (
                           <div className="space-y-6">
                               {/* Search Bar for Patient Directory */}
                               <div className="relative max-w-md">
                                   <i className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs"></i>
                                   <input 
                                       type="text"
                                       value={patientSearch}
                                       onChange={(e) => setPatientSearch(e.target.value)}
                                       placeholder="Search patient by Name or MRN..."
                                       className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:bg-white focus:border-indigo-500 focus:outline-none transition-all"
                                   />
                                   {patientSearch && (
                                       <button onClick={() => setPatientSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs">
                                           <i className="fa-solid fa-xmark"></i>
                                       </button>
                                   )}
                               </div>

                               <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                   {filteredPatients.map(p => (
                                       <div key={p.id} className="p-6 bg-slate-50/80 rounded-3xl hover:bg-indigo-50/60 border border-slate-100 hover:border-indigo-200 transition-all group cursor-pointer flex flex-col justify-between" onClick={() => setSelectedPatientId(p.id)}>
                                           <div>
                                               <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-lg text-indigo-500 mb-4 group-hover:scale-105 transition-transform shadow-xs">
                                                   <i className="fa-solid fa-folder-open"></i>
                                               </div>
                                               <h4 className="text-lg font-black text-slate-900 mb-1">{p.name}</h4>
                                               <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">ID: {p.id.slice(0, 8)}</p>
                                               <p className="text-[11px] font-bold text-indigo-600 uppercase tracking-wider mb-4">MRN: {p.mrn || 'N/A'}</p>
                                           </div>
                                           <button 
                                                onClick={(e) => { e.stopPropagation(); setSelectedPatientId(p.id); }} 
                                                className="w-full py-2.5 bg-white rounded-xl text-indigo-600 text-xs font-black uppercase tracking-wider hover:bg-indigo-600 hover:text-white transition-all shadow-2xs flex items-center justify-center gap-1.5"
                                           >
                                               <i className="fa-solid fa-eye text-xs"></i> View Health History
                                           </button>
                                       </div>
                                   ))}
                                   {filteredPatients.length === 0 && (
                                       <div className="col-span-full p-12 text-center bg-slate-50 rounded-3xl border border-dashed border-slate-200">
                                           <p className="text-slate-400 font-medium">No patients found matching your search.</p>
                                       </div>
                                   )}
                               </div>
                           </div>
                       ) : (
                           <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
                               <div className="flex justify-end">
                                   <Button onClick={() => setShowFollowUpModal(true)} className="bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/20 text-xs font-black">
                                       <i className="fa-solid fa-calendar-plus mr-2"></i> Schedule Complimentary Follow-up
                                   </Button>
                               </div>

                               <div className="space-y-6">
                                   <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest ml-1">Clinical Timeline & Medical Records</h3>
                                   {patientRecords.length === 0 ? (
                                       <div className="p-16 text-center bg-slate-50 rounded-3xl border border-dashed border-slate-200">
                                            <p className="text-slate-400 font-medium">No medical records found for this patient.</p>
                                       </div>
                                   ) : (
                                       <div className="grid gap-6">
                                           {patientRecords.map(record => (
                                               <div key={record.id} className="bg-slate-50/90 p-6 md:p-8 rounded-3xl border border-slate-100 hover:border-indigo-200 transition-all flex flex-col md:flex-row gap-6">
                                                   <div className="md:w-48 shrink-0">
                                                       <p className="text-sm font-black text-slate-500 uppercase tracking-wider">{record.date}</p>
                                                       <p className="text-[10px] font-bold text-indigo-500 uppercase mt-1">Authored by {record.doctorName}</p>
                                                       {record.lastEditedAt && (
                                                           <span className="inline-flex items-center gap-1 mt-2 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md">
                                                               <i className="fa-solid fa-clock-rotate-left"></i> Revised {new Date(record.lastEditedAt).toLocaleDateString()}
                                                           </span>
                                                       )}
                                                       <div className="mt-4 flex flex-col gap-2">
                                                           <button
                                                               onClick={() => setViewingMetadataRecord(record)}
                                                               className="w-full text-xs font-black text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 py-2 px-3 rounded-xl transition-all shadow-xs flex items-center justify-center gap-1.5"
                                                               title="View complete record details and metadata"
                                                           >
                                                               <i className="fa-solid fa-circle-info text-xs text-indigo-600"></i>
                                                               View Metadata
                                                           </button>
                                                           <button
                                                               onClick={() => openAmendRecordModal(record)}
                                                               className="w-full text-xs font-black text-indigo-600 bg-white hover:bg-indigo-50 border border-indigo-200 hover:border-indigo-300 py-2 px-3 rounded-xl transition-all shadow-xs flex items-center justify-center gap-1.5"
                                                           >
                                                               <i className="fa-solid fa-pen-to-square text-xs"></i>
                                                               Amend Record
                                                           </button>
                                                           {record.editHistory && record.editHistory.length > 0 && (
                                                               <button
                                                                   onClick={() => setExpandedAuditRecordId(expandedAuditRecordId === record.id ? null : record.id)}
                                                                   className="w-full text-[10px] font-bold text-slate-500 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 py-1.5 px-2 rounded-lg transition-all flex items-center justify-center gap-1"
                                                               >
                                                                   <i className="fa-solid fa-history text-xs"></i>
                                                                    {expandedAuditRecordId === record.id ? "Hide Audit" : "Audit Trail (" + record.editHistory.length + ")"}
                                                               </button>
                                                           )}
                                                       </div>
                                                   </div>
                                                   <div className="flex-grow space-y-6">
                                                       <div className="grid md:grid-cols-2 gap-6">
                                                           <div>
                                                               <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Clinical Impression (Diagnosis)</p>
                                                               <p className="text-xl font-black text-slate-900">{record.diagnosis}</p>
                                                           </div>
                                                           {record.vitalSigns && (
                                                               <div>
                                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Vital Signs</p>
                                                                    <p className="text-sm text-indigo-600 font-bold">{record.vitalSigns}</p>
                                                               </div>
                                                           )}
                                                       </div>

                                                       <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                                            <div>
                                                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Prescription (Rx)</p>
                                                                <p className="text-sm text-slate-600 font-medium whitespace-pre-wrap bg-white p-3 rounded-xl border border-slate-100">{record.prescription || 'N/A'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Treatment Plan</p>
                                                                <p className="text-sm text-slate-600 font-medium whitespace-pre-wrap bg-white p-3 rounded-xl border border-slate-100">{record.treatmentPlan || 'N/A'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Follow-up Instructions</p>
                                                                <p className="text-sm text-slate-600 font-medium whitespace-pre-wrap bg-white p-3 rounded-xl border border-slate-100">{record.followUpInstructions || 'N/A'}</p>
                                                            </div>
                                                       </div>

                                                       {(record.presentingSymptoms || record.historyOfPresentIllness) && (
                                                           <div className="grid sm:grid-cols-2 gap-6 p-4 bg-slate-100/50 rounded-2xl">
                                                                {record.presentingSymptoms && (
                                                                    <div>
                                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Presenting Symptoms</p>
                                                                        <p className="text-xs text-slate-600 font-bold">{record.presentingSymptoms}</p>
                                                                    </div>
                                                                )}
                                                                {record.historyOfPresentIllness && (
                                                                    <div>
                                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">HPI</p>
                                                                        <p className="text-xs text-slate-600 font-bold">{record.historyOfPresentIllness}</p>
                                                                    </div>
                                                                )}
                                                           </div>
                                                       )}

                                                       {record.notes && (
                                                           <div>
                                                               <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Additional Provider Notes</p>
                                                               <p className="text-sm text-slate-600 font-medium whitespace-pre-wrap italic">{record.notes}</p>
                                                           </div>
                                                       )}
                                                       {/* Audit Trail Accordion Panel */}
                                                       {expandedAuditRecordId === record.id && record.editHistory && record.editHistory.length > 0 && (
                                                           <div className="p-4 bg-amber-50/70 rounded-2xl border border-amber-200/90 space-y-3 animate-in fade-in duration-200">
                                                               <div className="flex items-center justify-between">
                                                                   <p className="text-xs font-black text-amber-900 uppercase tracking-wider flex items-center gap-1.5">
                                                                       <i className="fa-solid fa-clock-rotate-left"></i> Verified Revision History ({record.editHistory.length})
                                                                   </p>
                                                                   <span className="text-[10px] font-bold text-amber-700 bg-amber-100/80 px-2 py-0.5 rounded-full">Audit Compliant</span>
                                                               </div>
                                                               <div className="space-y-2 max-h-48 overflow-y-auto scrollbar-thin">
                                                                   {record.editHistory.map((edit, idx) => (
                                                                       <div key={idx} className="p-3 bg-white rounded-xl border border-amber-200/60 text-xs text-slate-700 space-y-1 shadow-xs">
                                                                           <div className="flex justify-between items-center text-[10px] font-black text-slate-400">
                                                                               <span className="text-indigo-600 font-bold">{edit.editedByDoctorName}</span>
                                                                               <span>{new Date(edit.editedAt).toLocaleString()}</span>
                                                                           </div>
                                                                           <p className="font-bold text-slate-800">Clinical Reason: {edit.summaryOfChanges || "Clinical documentation amended"}</p>
                                                                       </div>
                                                                   ))}
                                                               </div>
                                                           </div>
                                                       )}

                                                       {record.chatTranscript && record.chatTranscript.length > 0 && (
                                                           <div className="pt-4 border-t border-slate-200 space-y-2">
                                                               <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest flex items-center gap-1.5">
                                                                   <i className="fa-solid fa-comments"></i> Consultation Chat Transcript ({record.chatTranscript.length} messages)
                                                               </p>
                                                               <div className="p-4 bg-white rounded-2xl border border-slate-100 max-h-48 overflow-y-auto space-y-2 scrollbar-thin">
                                                                   {record.chatTranscript.map((msg, idx) => (
                                                                       <div key={msg.id || idx} className="text-xs p-2.5 rounded-xl bg-slate-50 border border-slate-100">
                                                                           <div className="flex justify-between items-center text-[10px] text-slate-400 font-bold mb-0.5">
                                                                               <span className="text-indigo-600">{msg.senderName || 'Participant'}</span>
                                                                               <span>{new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                                           </div>
                                                                           <p className="text-slate-700 font-medium">{msg.text}</p>
                                                                       </div>
                                                                   ))}
                                                               </div>
                                                           </div>
                                                       )}
                                                       {((record.attachments && record.attachments.length > 0) || record.attachmentUrl) && (
                                                           <div className="pt-4 border-t border-slate-200 space-y-2">
                                                               <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Attached Documents & Reports</p>
                                                               <div className="flex flex-wrap gap-2">
                                                                   {record.attachments && record.attachments.length > 0 ? (
                                                                       record.attachments.map((att, attIdx) => (
                                                                           <a
                                                                               key={attIdx}
                                                                               href={getSafeUrl(att.url)}
                                                                               target="_blank"
                                                                               rel="noopener noreferrer"
                                                                               className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-xs transition-all border border-indigo-100 shadow-2xs"
                                                                           >
                                                                               <i className="fa-solid fa-paperclip text-[10px]"></i>
                                                                               <span className="truncate max-w-[200px]">{att.name}</span>
                                                                               {att.size && <span className="text-[10px] text-slate-400 font-normal">({(att.size / 1024).toFixed(1)} KB)</span>}
                                                                           </a>
                                                                       ))
                                                                   ) : (
                                                                       <a 
                                                                            href={getSafeUrl(record.attachmentUrl)} 
                                                                            target="_blank" 
                                                                            rel="noopener noreferrer"
                                                                            className="inline-flex items-center gap-2 text-indigo-600 font-black text-[10px] uppercase tracking-widest hover:text-indigo-800"
                                                                       >
                                                                           <i className="fa-solid fa-paperclip"></i> {record.attachmentName || 'View Attachment'}
                                                                       </a>
                                                                   )}
                                                               </div>
                                                           </div>
                                                       )}
                                                   </div>
                                               </div>
                                           ))}
                                       </div>
                                   )}
                               </div>
                           </div>
                       )}
                   </div>
                )}

                {tab === 'working' && (
                    <div className="bg-white p-8 md:p-12 rounded-[3rem] md:rounded-[4rem] border border-slate-50 shadow-sm space-y-12">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                            <SectionTitle title="Consultation Hours" subtitle="Define your recurring clinical consultation windows." />
                            <div className="bg-indigo-50 px-4 py-2 rounded-xl text-xs font-black text-indigo-600 uppercase tracking-widest flex items-center gap-2">
                                <i className="fa-solid fa-earth-americas"></i> 
                                Configured for: {editProfile.timezone}
                            </div>
                        </div>

                        {/* Special Requests Toggle Option */}
                        <div className="p-6 md:p-8 bg-gradient-to-r from-amber-50/80 via-white to-orange-50/50 rounded-3xl border-2 border-amber-200/80 shadow-md flex flex-col md:flex-row items-start md:items-center justify-between gap-6 max-w-4xl">
                            <div className="space-y-2 flex-1">
                                <div className="flex flex-wrap items-center gap-3">
                                    <span className={`w-3.5 h-3.5 rounded-full ${allowSpecialRequests ? 'bg-amber-500 animate-pulse' : 'bg-slate-400'}`}></span>
                                    <h4 className="font-black text-base md:text-lg text-slate-900 tracking-tight">
                                        Special Request Slots Setting
                                    </h4>
                                    <span className={`px-3 py-1 rounded-full text-xs font-black tracking-wider uppercase shadow-xs ${
                                        allowSpecialRequests 
                                            ? 'bg-amber-100 text-amber-900 border border-amber-300' 
                                            : 'bg-slate-100 text-slate-600 border border-slate-300'
                                    }`}>
                                        {allowSpecialRequests ? 'ENABLED (ON)' : 'DISABLED (OFF)'}
                                    </span>
                                </div>
                                <p className="text-xs md:text-sm text-slate-600 leading-relaxed font-medium max-w-2xl">
                                    Allow patients to request appointments on days you mark as offline/non-working. When turned ON, standard consultation time slots will be opened for special request bookings on offline days.
                                </p>
                            </div>

                            <div className="flex items-center gap-3 shrink-0 self-start md:self-center bg-white p-2.5 rounded-2xl border border-amber-200/60 shadow-xs">
                                <span className={`text-xs font-black uppercase tracking-wider ${allowSpecialRequests ? 'text-amber-800' : 'text-slate-500'}`}>
                                    {allowSpecialRequests ? 'ENABLED' : 'DISABLED'}
                                </span>
                                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                                    <input 
                                        type="checkbox" 
                                        className="sr-only peer"
                                        checked={allowSpecialRequests}
                                        onChange={e => setAllowSpecialRequests(e.target.checked)}
                                    />
                                    <div className="w-14 h-7 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[3px] after:left-[3px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500 shadow-inner"></div>
                                </label>
                            </div>
                        </div>

                        <div className="grid gap-4 md:gap-6 max-w-4xl">
                            {days.map(day => (
                                <div key={day} className="flex flex-col md:flex-row items-start md:items-center gap-4 p-6 bg-slate-50 rounded-3xl border border-slate-100 group">
                                    <div className="w-full md:w-48 flex flex-col justify-between">
                                        <p className="font-black text-slate-900">{day}</p>
                                        <label className="flex items-center gap-2 mt-1 cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                                                checked={workingHours[day]?.active ?? false}
                                                onChange={e => setWorkingHours(prev => ({
                                                    ...prev,
                                                    [day]: { ...(prev[day] || DEFAULT_WORKING_HOURS[day]), active: e.target.checked }
                                                }))}
                                            />
                                            <span className={`text-xs font-black uppercase tracking-widest ${workingHours[day]?.active ? 'text-indigo-600' : 'text-slate-400'}`}>
                                                {workingHours[day]?.active ? 'Active Shift' : 'Offline / Off'}
                                            </span>
                                        </label>
                                    </div>
                                    <div className={`flex flex-wrap items-center gap-4 w-full md:w-auto transition-opacity ${workingHours[day]?.active ? 'opacity-100' : 'opacity-30 pointer-events-none'}`}>
                                        <div className="space-y-1 flex-grow">
                                            <p className="text-xs font-black uppercase text-slate-400 tracking-widest ml-1">Start Time</p>
                                            <input 
                                                type="time" 
                                                className="w-full p-3 bg-white rounded-xl border border-slate-200 font-bold text-sm" 
                                                value={workingHours[day]?.start || '09:00'}
                                                onChange={e => setWorkingHours(prev => ({
                                                    ...prev,
                                                    [day]: { ...(prev[day] || DEFAULT_WORKING_HOURS[day]), start: e.target.value }
                                                }))}
                                            />
                                        </div>
                                        <span className="hidden md:inline font-bold text-slate-300 pt-4">to</span>
                                        <div className="space-y-1 flex-grow">
                                            <p className="text-xs font-black uppercase text-slate-400 tracking-widest ml-1">End Time</p>
                                            <input 
                                                type="time" 
                                                className="w-full p-3 bg-white rounded-xl border border-slate-200 font-bold text-sm" 
                                                value={workingHours[day]?.end || '17:00'}
                                                onChange={e => setWorkingHours(prev => ({
                                                    ...prev,
                                                    [day]: { ...(prev[day] || DEFAULT_WORKING_HOURS[day]), end: e.target.value }
                                                }))}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Save Practice Hours Button */}
                        <div className="pt-2">
                            <Button onClick={saveWorkingHours} disabled={isSaving} className="w-full max-w-xl py-5 text-base shadow-xl shadow-indigo-100 hover:shadow-indigo-200 transition-all flex items-center justify-center gap-3">
                                {isSaving ? (
                                    <i className="fa-solid fa-circle-notch fa-spin"></i>
                                ) : (
                                    <i className="fa-solid fa-clock"></i>
                                )}
                                {isSaving ? 'Syncing Schedule...' : 'Save Practice Hours'}
                            </Button>
                        </div>

                        {/* Visual Availability Calendar & Custom Slot Management */}
                        <VisualAvailabilityCalendar 
                          user={user}
                          busyDate={busyDate}
                          setBusyDate={setBusyDate}
                          allAppointments={allAppointments}
                          appointments={appointments}
                          toggleDoctorBusySlot={toggleDoctorBusySlot}
                          setDoctorDayBusy={setDoctorDayBusy}
                        />
                    </div>
                )}

                {tab === 'profile' && (
                    <div className="grid lg:grid-cols-2 gap-10">
                        <div className="bg-white p-8 md:p-12 rounded-[3rem] md:rounded-[4rem] border border-slate-50 shadow-sm space-y-8">
                            <SectionTitle title="Public Profile" subtitle="Your information visible to patients." />
                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-4">Display Name</label>
                                    <input className="w-full p-5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-lg" value={editProfile.name} onChange={e => setEditProfile({...editProfile, name: e.target.value})} />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-4">Visit Price</label>
                                    <div className={`p-5 rounded-2xl border-2 font-bold flex justify-between items-center ${globalSettings.paymentsEnabled ? 'bg-indigo-50 border-indigo-100 text-indigo-900' : 'bg-rose-50 border-rose-100 text-rose-900'}`}>
                                        <div className="flex flex-col">
                                            <span>Current Rate:</span>
                                            {!globalSettings.paymentsEnabled && <span className="text-[10px] font-black uppercase text-rose-500">Complimentary Access</span>}
                                        </div>
                                        <div className="text-right">
                                            <span className={`text-xl font-black ${!globalSettings.paymentsEnabled ? 'line-through opacity-50' : ''}`}>
                                                {globalSettings.platformCurrency} {user.sessionRate || 50}
                                            </span>
                                            {!globalSettings.paymentsEnabled && <p className="text-sm font-black">$0 to Patients</p>}
                                        </div>
                                    </div>
                                    <p className="text-[10px] text-slate-400 ml-4">Please contact our support team to adjust your standard consultation fee or status.</p>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-4">Local Timezone</label>
                                    <div className="p-5 bg-slate-50 rounded-2xl border-2 border-slate-100 font-bold text-slate-600 flex justify-between items-center">
                                        <span>{editProfile.timezone}</span>
                                        <Button variant="ghost" className="py-1 px-3 text-xs" onClick={() => setEditProfile({...editProfile, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone})}>
                                            Reset to Current
                                        </Button>
                                    </div>
                                    <p className="text-[10px] text-slate-400 ml-4">Your availability is calculated based on this timezone.</p>
                                </div>
                                <div className="space-y-4">
                                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-4">Areas of Expertise (Specialties)</label>
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {editProfile.specialties.map(s => (
                                            <span key={s} className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-black uppercase flex items-center gap-2 shadow-md">
                                                {s}
                                                <button onClick={() => removeSpecialty(s)} className="hover:text-rose-200 transition-colors"><i className="fa-solid fa-circle-xmark"></i></button>
                                            </span>
                                        ))}
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-4 bg-slate-50 rounded-3xl border border-slate-100">
                                        {commonSpecialties.map(cs => (
                                            <button 
                                                key={cs} 
                                                onClick={() => addSpecialty(cs)}
                                                disabled={editProfile.specialties.includes(cs)}
                                                className={`px-3 py-2 rounded-xl text-xs font-black uppercase transition-all ${
                                                    editProfile.specialties.includes(cs) 
                                                    ? 'bg-indigo-100 text-indigo-300 opacity-50' 
                                                    : 'bg-white text-slate-500 border border-slate-100 hover:border-indigo-400 hover:text-indigo-600'
                                                }`}
                                            >
                                                {cs}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="flex gap-2">
                                        <input className="flex-grow p-4 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-sm" placeholder="Add other specialty..." value={newSpecialty} onChange={e => setNewSpecialty(e.target.value)} onKeyPress={e => e.key === 'Enter' && addSpecialty()} />
                                        <button onClick={() => addSpecialty()} className="w-14 h-14 bg-emerald-500 text-white rounded-2xl hover:bg-emerald-600 transition-all shadow-md"><i className="fa-solid fa-plus"></i></button>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-4">Doctor Bio</label>
                                    <textarea className="w-full p-6 bg-slate-50 rounded-[2rem] border-2 border-transparent focus:border-indigo-500 outline-none font-bold h-32 resize-none leading-relaxed text-base" value={editProfile.bio} onChange={e => setEditProfile({...editProfile, bio: e.target.value})} />
                                </div>
                                <div className="space-y-4 pt-4 border-t border-slate-50">
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Date of Birth</label>
                                            <input type="date" className="w-full p-4 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold" value={editProfile.dob || ''} onChange={e => setEditProfile({...editProfile, dob: e.target.value})} />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Gender</label>
                                            <select className="w-full p-4 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold appearance-none" value={editProfile.gender || ''} onChange={e => setEditProfile({...editProfile, gender: e.target.value})}>
                                                <option value="">Select Gender</option>
                                                <option value="male">Male</option>
                                                <option value="female">Female</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Phone Number</label>
                                            <input className="w-full p-4 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold" value={editProfile.phone || ''} onChange={e => setEditProfile({...editProfile, phone: e.target.value})} />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Professional Office Address</label>
                                            <input className="w-full p-4 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold" value={editProfile.address || ''} onChange={e => setEditProfile({...editProfile, address: e.target.value})} />
                                        </div>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-4 pt-2">
                                        <div className="space-y-1">
                                            <div className="flex items-center justify-between ml-4">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Medical License ID / Registration #</label>
                                                <span className="text-[9px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">Doctor License</span>
                                            </div>
                                            <input 
                                                className="w-full p-4 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold placeholder:text-slate-400 text-slate-800" 
                                                placeholder="e.g. MD-84920 or GMC-7491024"
                                                value={editProfile.licenseNumber || ''} 
                                                onChange={e => setEditProfile({...editProfile, licenseNumber: e.target.value})} 
                                            />
                                            <p className="text-[10px] text-slate-400 ml-4">Shown on your official digital prescriptions & patient records.</p>
                                        </div>
                                        <div className="space-y-1">
                                            <div className="flex items-center justify-between ml-4">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Rovita Telehealth License ID</label>
                                                <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">Humane ID</span>
                                            </div>
                                            <div className="w-full p-4 bg-slate-100/80 rounded-2xl border border-slate-200 font-mono font-bold text-slate-800 flex items-center justify-between">
                                                <span>{user?.rovitaLicenseId || formatHumaneRovitaLicenseId(user)}</span>
                                                <i className="fa-solid fa-id-card text-indigo-500"></i>
                                            </div>
                                            <p className="text-[10px] text-slate-400 ml-4">Verified Rovita telehealth clinical credential identifier.</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="sticky bottom-0 left-0 right-0 z-10 -mx-8 md:-mx-12 px-8 md:px-12 py-6 bg-white/80 backdrop-blur-xl border-t border-slate-100 rounded-b-[3rem] md:rounded-b-[4rem]">
                                    <Button onClick={saveProfileChanges} disabled={isSaving} className="w-full py-6 text-xl shadow-2xl shadow-indigo-200/40 hover:shadow-indigo-200/60 transition-all flex items-center justify-center gap-3">
                                        {isSaving ? (
                                            <i className="fa-solid fa-circle-notch fa-spin"></i>
                                        ) : (
                                            <i className="fa-solid fa-floppy-disk"></i>
                                        )}
                                        {isSaving ? 'Saving Profile...' : 'Save Profile Changes'}
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-8 md:p-12 rounded-[3rem] md:rounded-[4rem] border border-slate-50 shadow-sm space-y-8 text-center flex flex-col items-center justify-center">
                            <div className="w-24 h-24 bg-indigo-50 text-indigo-500 rounded-[2rem] flex items-center justify-center text-4xl mb-6 shadow-sm"><i className="fa-solid fa-id-badge"></i></div>
                            <SectionTitle title="Verification" subtitle="Upload proof of qualification to get your verified badge." centered />
                            <div className="p-8 bg-indigo-50/50 rounded-[3rem] border border-indigo-100 w-full">
                                {user.credentialUrl && (
                                    <div className="mb-6 p-4 bg-white rounded-2xl text-xs font-black text-emerald-500 uppercase flex items-center justify-center gap-2 shadow-sm">
                                        <i className="fa-solid fa-circle-check"></i> License Uploaded
                                    </div>
                                )}
                                <label className="w-full bg-white text-indigo-600 py-6 rounded-[2rem] font-black text-sm cursor-pointer border-2 border-dashed border-indigo-200 hover:border-indigo-500 transition-all block">
                                    {user.credentialUrl ? 'Replace Proof' : 'Upload Medical License / ID'}
                                    <input type="file" className="hidden" onChange={handleCredentialUpload} accept=".pdf,.jpg,.jpeg,.png" />
                                </label>
                            </div>
                        </div>
                    </div>
                )}


                {tab === 'security' && (
                    <div className="max-w-2xl bg-white p-6 sm:p-10 md:p-12 rounded-[2.5rem] md:rounded-[3.5rem] border border-slate-100 shadow-sm space-y-8 mx-auto">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-amber-50 text-amber-500 rounded-3xl flex items-center justify-center text-2xl mx-auto mb-4 shadow-2xs">
                                <i className="fa-solid fa-lock"></i>
                            </div>
                            <SectionTitle title="Security & Privacy" subtitle="Manage your login credentials safely." centered />
                        </div>

                        {docSecurityFeedback && (
                            <div 
                                className={`p-4 rounded-2xl text-xs font-bold flex items-start gap-3 animate-in fade-in ${
                                    docSecurityFeedback.type === 'success' 
                                        ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
                                        : 'bg-rose-50 text-rose-800 border border-rose-200'
                                }`}
                                role="alert"
                            >
                                <i className={`fa-solid ${docSecurityFeedback.type === 'success' ? 'fa-circle-check text-emerald-600' : 'fa-circle-exclamation text-rose-600'} text-base shrink-0 mt-0.5`}></i>
                                <span className="leading-relaxed">{docSecurityFeedback.text}</span>
                            </div>
                        )}

                        {/* Direct Password Change Form */}
                        <form 
                            onSubmit={async (e) => {
                                e.preventDefault();
                                setDocSecurityFeedback(null);
                                if (docNewPass.length < 6) {
                                    setDocSecurityFeedback({ type: 'error', text: 'New password must be at least 6 characters long.' });
                                    return;
                                }
                                if (docNewPass !== docConfirmPass) {
                                    setDocSecurityFeedback({ type: 'error', text: 'New password and confirmation do not match.' });
                                    return;
                                }
                                setDocPassSubmitting(true);
                                try {
                                    await changeUserPassword(docCurrentPass, docNewPass);
                                    setDocSecurityFeedback({ type: 'success', text: 'Password updated successfully!' });
                                    setDocCurrentPass('');
                                    setDocNewPass('');
                                    setDocConfirmPass('');
                                } catch (err: any) {
                                    setDocSecurityFeedback({ type: 'error', text: err.message || 'Failed to update password.' });
                                } finally {
                                    setDocPassSubmitting(false);
                                }
                            }}
                            className="p-6 sm:p-8 bg-slate-50 rounded-3xl border border-slate-100 space-y-4 text-left"
                        >
                            <h4 className="font-black text-slate-800 text-base flex items-center gap-2">
                                <i className="fa-solid fa-key text-indigo-600"></i>
                                Change Login Password
                            </h4>

                            <div>
                                <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1">Current Password</label>
                                <div className="relative">
                                    <input 
                                        type={showDocCurrentPass ? "text" : "password"} 
                                        value={docCurrentPass} 
                                        onChange={e => setDocCurrentPass(e.target.value)} 
                                        placeholder="Enter current password"
                                        className="w-full px-4 py-3 pr-10 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                    <button 
                                        type="button" 
                                        onClick={() => setShowDocCurrentPass(!showDocCurrentPass)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                                    >
                                        <i className={`fa-solid ${showDocCurrentPass ? 'fa-eye-slash' : 'fa-eye'} text-xs`}></i>
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1">New Password</label>
                                    <div className="relative">
                                        <input 
                                            type={showDocNewPass ? "text" : "password"} 
                                            required
                                            value={docNewPass} 
                                            onChange={e => setDocNewPass(e.target.value)} 
                                            placeholder="Min. 6 characters"
                                            className="w-full px-4 py-3 pr-10 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        />
                                        <button 
                                            type="button" 
                                            onClick={() => setShowDocNewPass(!showDocNewPass)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                                        >
                                            <i className={`fa-solid ${showDocNewPass ? 'fa-eye-slash' : 'fa-eye'} text-xs`}></i>
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[11px] font-black uppercase tracking-wider text-slate-500 mb-1">Confirm New Password</label>
                                    <div className="relative">
                                        <input 
                                            type={showDocConfirmPass ? "text" : "password"} 
                                            required
                                            value={docConfirmPass} 
                                            onChange={e => setDocConfirmPass(e.target.value)} 
                                            placeholder="Re-type new password"
                                            className="w-full px-4 py-3 pr-10 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        />
                                        <button 
                                            type="button" 
                                            onClick={() => setShowDocConfirmPass(!showDocConfirmPass)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                                        >
                                            <i className={`fa-solid ${showDocConfirmPass ? 'fa-eye-slash' : 'fa-eye'} text-xs`}></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <button 
                                type="submit" 
                                disabled={docPassSubmitting}
                                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-sm flex items-center gap-2 disabled:opacity-50 active:scale-95 mt-2"
                            >
                                {docPassSubmitting ? (
                                    <>
                                        <i className="fa-solid fa-spinner fa-spin"></i>
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <i className="fa-solid fa-check"></i>
                                        Update Password
                                    </>
                                )}
                            </button>
                        </form>

                        {/* Email Reset Link Option */}
                        <div className="p-6 sm:p-8 bg-slate-50 rounded-3xl border border-slate-100 text-left space-y-3">
                            <h4 className="font-black text-slate-800 text-sm flex items-center gap-2">
                                <i className="fa-solid fa-envelope text-indigo-600"></i>
                                Email Password Reset Link
                            </h4>
                            <p className="text-xs text-slate-500 font-medium">
                                We can send a secure reset link to your registered email: <strong>{user?.email}</strong>.
                            </p>
                            <button
                                type="button"
                                disabled={docResetSubmitting || !user?.email}
                                onClick={async () => {
                                    if (!user?.email) return;
                                    setDocResetSubmitting(true);
                                    setDocSecurityFeedback(null);
                                    try {
                                        await resetUserPassword(user.email);
                                        setDocSecurityFeedback({ 
                                            type: 'success', 
                                            text: `A password reset link has been dispatched to ${user.email}. Check your inbox.` 
                                        });
                                    } catch (err: any) {
                                        setDocSecurityFeedback({ 
                                            type: 'error', 
                                            text: err.message || 'Could not send reset email.' 
                                        });
                                    } finally {
                                        setDocResetSubmitting(false);
                                    }
                                }}
                                className="px-4 py-2.5 bg-white hover:bg-slate-100 text-slate-700 font-bold text-xs rounded-xl border border-slate-200 transition-all flex items-center gap-2 shadow-2xs active:scale-95 disabled:opacity-50"
                            >
                                {docResetSubmitting ? (
                                    <>
                                        <i className="fa-solid fa-spinner fa-spin"></i>
                                        Sending Link...
                                    </>
                                ) : (
                                    <>
                                        <i className="fa-solid fa-paper-plane text-indigo-600"></i>
                                        Send Reset Link to {user?.email}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
            {/* Appointment, Referral, and Record Modals */}
            {editingApp && (
                <div className="fixed inset-0 z-[500] flex items-center justify-center p-2 sm:p-4 md:p-6 bg-slate-900/60 backdrop-blur-md overflow-y-auto animate-in fade-in duration-300">
                    <div className="bg-white w-full max-w-lg rounded-[1.8rem] sm:rounded-[2.5rem] md:rounded-[3rem] shadow-2xl animate-in zoom-in-95 duration-300 max-h-[calc(100dvh-1.25rem)] sm:max-h-[calc(100dvh-2rem)] md:max-h-[85vh] my-auto flex flex-col overflow-hidden border border-slate-100 relative">
                        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-50 bg-slate-50/50 flex justify-between items-center shrink-0">
                            <div>
                                <h3 className="text-lg sm:text-xl md:text-2xl font-black text-slate-900">Modify Appointment</h3>
                                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-0.5">{editingApp.patientName}</p>
                            </div>
                            <button onClick={() => setEditingApp(null)} className="text-slate-300 hover:text-slate-500 transition-colors p-2 rounded-xl"><i className="fa-solid fa-xmark text-xl"></i></button>
                        </div>
                        <div className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6 overflow-y-auto flex-1 scrollbar-thin min-h-0">
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Scheduled Date</label>
                                <input type="date" className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-sm sm:text-base" value={editingApp.date} onChange={e => setEditingApp(prev => prev ? {...prev, date: e.target.value} : null)} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Scheduled Time</label>
                                <input type="time" className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-sm sm:text-base" value={editingApp.time} onChange={e => setEditingApp(prev => prev ? {...prev, time: e.target.value} : null)} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Current Status</label>
                                <select className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none font-bold appearance-none text-sm sm:text-base" value={editingApp.status} onChange={e => setEditingApp(prev => prev ? {...prev, status: e.target.value as any} : null)}>
                                    <option value="upcoming">Upcoming</option>
                                    <option value="completed">Completed</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>
                        </div>
                        <div className="p-4 sm:p-6 md:p-8 bg-slate-50/90 backdrop-blur-md flex gap-3 sm:gap-4 shrink-0 border-t border-slate-100 z-10">
                            <Button onClick={() => setEditingApp(null)} variant="ghost" className="flex-1 py-3 text-sm">Back</Button>
                            <Button onClick={async () => { 
                                const current = editingApp;
                                if (current) {
                                    await adminUpdateAppointment(current.id, { date: current.date, time: current.time, status: current.status }); 
                                    setEditingApp(null); 
                                    showToast("Appointment Updated", "Appointment details updated successfully!", "success"); 
                                }
                            }} className="flex-[2] py-3 text-sm">Save Changes</Button>
                        </div>
                    </div>
                </div>
            )}
            {referringAppointment && (
                <div className="fixed inset-0 z-[500] flex items-center justify-center p-2 sm:p-4 md:p-6 bg-slate-900/60 backdrop-blur-md overflow-y-auto animate-in fade-in duration-300">
                    <div className="bg-white w-full max-w-lg rounded-[1.8rem] sm:rounded-[2.5rem] md:rounded-[3rem] shadow-2xl animate-in zoom-in-95 duration-300 max-h-[calc(100dvh-1.25rem)] sm:max-h-[calc(100dvh-2rem)] md:max-h-[85vh] my-auto flex flex-col overflow-hidden border border-slate-100 relative">
                        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-50 bg-slate-50/50 flex justify-between items-center shrink-0">
                            <div>
                                <h3 className="text-lg sm:text-xl md:text-2xl font-black text-slate-900">Doctor Referral</h3>
                                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-0.5">Patient: {referringAppointment.patientName}</p>
                            </div>
                            <button onClick={() => setReferringAppointment(null)} className="text-slate-300 hover:text-slate-500 transition-colors p-2 rounded-xl"><i className="fa-solid fa-xmark text-xl"></i></button>
                        </div>
                        <div className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6 overflow-y-auto flex-1 scrollbar-thin min-h-0">
                            <div className="bg-amber-50 p-3.5 sm:p-4 rounded-xl border border-amber-100">
                                <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest">Administrative Notice</p>
                                <p className="text-xs font-bold text-amber-700 mt-1 italic">The appointment will be reassigned to the selected clinic schedule.</p>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Target Doctor</label>
                                <select className="w-full p-3.5 bg-slate-50 rounded-2xl outline-none font-bold text-sm sm:text-base" value={referToDocId} onChange={e => setReferToDocId(e.target.value)}>
                                    <option value="">Choose Doctor</option>
                                    {doctors.filter(d => d.id !== user.id && !isUserDisabled(d)).map(d => (
                                        <option key={d.id} value={d.id}>{d.name} ({d.specialties?.join(', ') || 'GP'})</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Handover Notes</label>
                                <textarea className="w-full p-3.5 bg-slate-50 rounded-2xl h-28 font-bold resize-none leading-relaxed text-xs sm:text-sm" placeholder="Reason for transfer..." value={referNotes} onChange={e => setReferNotes(e.target.value)} />
                            </div>
                        </div>
                        <div className="p-4 sm:p-6 md:p-8 bg-slate-50/90 backdrop-blur-md flex gap-3 sm:gap-4 shrink-0 border-t border-slate-100 z-10">
                            <Button onClick={() => setReferringAppointment(null)} variant="ghost" className="flex-1 py-3 text-sm">Cancel</Button>
                            <Button onClick={confirmReferral} disabled={!referToDocId} className="flex-[2] py-3 text-sm shadow-lg shadow-indigo-100 flex items-center justify-center gap-2">
                                <i className="fa-solid fa-paper-plane"></i>
                                Issue Secure Referral
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {showRecordModal && (
                <div 
                    className="fixed inset-0 z-[500] flex items-center justify-center p-2 sm:p-4 md:p-6 bg-slate-900/60 backdrop-blur-md overflow-y-auto animate-in fade-in duration-300 font-['Plus_Jakarta_Sans']"
                    onClick={(e) => e.target === e.currentTarget && setShowRecordModal(false)}
                >
                    <div className="bg-white w-full max-w-4xl rounded-[2rem] sm:rounded-[2.5rem] md:rounded-[3rem] shadow-2xl relative animate-in zoom-in-95 duration-300 max-h-[calc(100dvh-1.25rem)] sm:max-h-[calc(100dvh-2rem)] md:max-h-[90vh] my-auto flex flex-col overflow-hidden border border-slate-100">
                        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-slate-50/70 flex justify-between items-center shrink-0">
                            <div>
                                <span className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-[10px] font-black uppercase tracking-widest inline-block mb-1.5 border border-indigo-100/60">
                                    Official Clinical Documentation
                                </span>
                                <h3 className="text-lg sm:text-xl md:text-2xl font-black text-slate-900 tracking-tight">
                                    Post-Consultation Health Record & e-Prescription
                                </h3>
                                <p className="text-slate-500 font-medium text-xs mt-0.5">
                                    Author official clinical assessment, vitals, management plan, and verifiable digital prescription (Rx).
                                </p>
                            </div>
                            <button 
                                onClick={() => setShowRecordModal(false)} 
                                className="text-slate-400 hover:text-slate-600 transition-colors bg-white w-10 h-10 rounded-2xl flex items-center justify-center shadow-xs border border-slate-200"
                            >
                                <i className="fa-solid fa-xmark text-lg"></i>
                            </button>
                        </div>
                        
                        <div className="p-4 sm:p-6 md:p-8 space-y-8 overflow-y-auto flex-1 scrollbar-thin min-h-0 bg-white">
                            {/* Patient Selector */}
                            <div className="p-4 bg-indigo-50/40 rounded-2xl border border-indigo-100/60 space-y-2">
                                <label className="text-[10px] font-black text-indigo-900 uppercase tracking-widest block">
                                    Select Patient for Clinical Record
                                </label>
                                <select 
                                    className="w-full p-3.5 bg-white rounded-xl border border-slate-200 focus:border-indigo-500 outline-none font-bold text-sm text-slate-800 shadow-xs" 
                                    value={recordPatientId} 
                                    onChange={e => setRecordPatientId(e.target.value)}
                                >
                                    <option value="">-- Choose Patient --</option>
                                    {associatedPatients.map(p => (
                                        <option key={p.id} value={p.id}>{p.name} {p.mrn ? `(MRN: ${p.mrn})` : ''}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Section 1: Subjective */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-indigo-700 font-black text-xs uppercase tracking-widest border-b border-slate-100 pb-2">
                                    <i className="fa-solid fa-clipboard-user text-sm"></i>
                                    <span>1. Subjective History & Symptoms</span>
                                </div>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Chief Complaint / Presenting Symptoms</label>
                                        <input 
                                            type="text"
                                            className="w-full p-3.5 bg-slate-50 rounded-2xl border border-slate-200 focus:bg-white focus:border-indigo-500 outline-none font-semibold text-xs text-slate-800" 
                                            placeholder="e.g., Persistent dry cough, fever for 3 days" 
                                            value={recordData.presentingSymptoms} 
                                            onChange={e => setRecordData({...recordData, presentingSymptoms: e.target.value})} 
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">History of Present Illness (HPI)</label>
                                        <textarea 
                                            className="w-full p-3.5 bg-slate-50 rounded-2xl h-20 font-semibold resize-none outline-none focus:bg-white focus:border-indigo-500 border border-slate-200 text-xs text-slate-800 leading-relaxed" 
                                            placeholder="Onset, duration, severity, aggravating/relieving factors..."
                                            value={recordData.history} 
                                            onChange={e => setRecordData({...recordData, history: e.target.value})} 
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Past Medical & Surgical History</label>
                                        <input 
                                            type="text"
                                            className="w-full p-3.5 bg-slate-50 rounded-2xl border border-slate-200 focus:bg-white focus:border-indigo-500 outline-none font-semibold text-xs text-slate-800" 
                                            placeholder="e.g., Hypertension (2018), appendectomy (2014)" 
                                            value={recordData.pastHistory} 
                                            onChange={e => setRecordData({...recordData, pastHistory: e.target.value})} 
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Social & Family History</label>
                                        <input 
                                            type="text"
                                            className="w-full p-3.5 bg-slate-50 rounded-2xl border border-slate-200 focus:bg-white focus:border-indigo-500 outline-none font-semibold text-xs text-slate-800" 
                                            placeholder="e.g., Non-smoker, maternal history of T2D" 
                                            value={recordData.socialHistory} 
                                            onChange={e => setRecordData({...recordData, socialHistory: e.target.value})} 
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section 2: Objective */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-indigo-700 font-black text-xs uppercase tracking-widest border-b border-slate-100 pb-2">
                                    <i className="fa-solid fa-stethoscope text-sm"></i>
                                    <span>2. Objective Findings & Vitals</span>
                                </div>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Vital Signs (BP, HR, Temp, SpO2)</label>
                                        <input 
                                            type="text"
                                            className="w-full p-3.5 bg-slate-50 rounded-2xl border border-slate-200 focus:bg-white focus:border-indigo-500 outline-none font-semibold text-xs text-slate-800" 
                                            placeholder="e.g., BP 120/80 mmHg, HR 72 bpm, SpO2 99%, Temp 98.6°F" 
                                            value={recordData.vitals} 
                                            onChange={e => setRecordData({...recordData, vitals: e.target.value})} 
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Physical Examination Findings</label>
                                        <input 
                                            type="text"
                                            className="w-full p-3.5 bg-slate-50 rounded-2xl border border-slate-200 focus:bg-white focus:border-indigo-500 outline-none font-semibold text-xs text-slate-800" 
                                            placeholder="e.g., Clear breath sounds bilaterally, no pharyngeal erythema" 
                                            value={recordData.physicalExam} 
                                            onChange={e => setRecordData({...recordData, physicalExam: e.target.value})} 
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Diagnostic Lab Results</label>
                                        <input 
                                            type="text"
                                            className="w-full p-3.5 bg-slate-50 rounded-2xl border border-slate-200 focus:bg-white focus:border-indigo-500 outline-none font-semibold text-xs text-slate-800" 
                                            placeholder="e.g., CBC normal, HbA1c 5.6%" 
                                            value={recordData.labReports} 
                                            onChange={e => setRecordData({...recordData, labReports: e.target.value})} 
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Imaging Studies</label>
                                        <input 
                                            type="text"
                                            className="w-full p-3.5 bg-slate-50 rounded-2xl border border-slate-200 focus:bg-white focus:border-indigo-500 outline-none font-semibold text-xs text-slate-800" 
                                            placeholder="e.g., Chest X-Ray: No focal consolidation" 
                                            value={recordData.imaging} 
                                            onChange={e => setRecordData({...recordData, imaging: e.target.value})} 
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section 3: Assessment & Plan */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-indigo-700 font-black text-xs uppercase tracking-widest border-b border-slate-100 pb-2">
                                    <i className="fa-solid fa-notes-medical text-sm"></i>
                                    <span>3. Clinical Assessment & Management Plan</span>
                                </div>
                                <div className="space-y-4">
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                                            <span>Clinical Diagnosis / Impression</span>
                                            <span className="text-rose-500 font-bold">*Required</span>
                                        </label>
                                        <input 
                                            type="text"
                                            className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:bg-white focus:border-indigo-500 outline-none font-black text-sm text-slate-900" 
                                            placeholder="e.g., Acute Upper Respiratory Tract Infection (URTI)" 
                                            value={recordData.diagnosis} 
                                            onChange={e => setRecordData({...recordData, diagnosis: e.target.value})} 
                                        />
                                    </div>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Treatment Plan & Patient Care</label>
                                            <textarea 
                                                className="w-full p-3.5 bg-slate-50 rounded-2xl h-24 font-semibold resize-none outline-none focus:bg-white focus:border-indigo-500 border border-slate-200 text-xs text-slate-800 leading-relaxed" 
                                                placeholder="Hydration, rest, symptom monitoring..."
                                                value={recordData.treatmentPlan} 
                                                onChange={e => setRecordData({...recordData, treatmentPlan: e.target.value})} 
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Official Rovita e-Prescription (Rx)</label>
                                            <textarea 
                                                className="w-full p-3.5 bg-indigo-50/50 rounded-2xl h-24 font-bold resize-none outline-none focus:bg-white focus:border-indigo-500 border border-indigo-200 text-xs text-indigo-950 leading-relaxed" 
                                                placeholder="e.g., Amoxicillin 500mg - 1 capsule PO TID for 7 days&#10;Paracetamol 500mg - 1 tab PO Q6H PRN fever"
                                                value={recordData.prescription} 
                                                onChange={e => setRecordData({...recordData, prescription: e.target.value})} 
                                            />
                                        </div>
                                    </div>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Follow-Up Instructions & Schedule</label>
                                            <input 
                                                type="text"
                                                className="w-full p-3.5 bg-slate-50 rounded-2xl border border-slate-200 focus:bg-white focus:border-indigo-500 outline-none font-semibold text-xs text-slate-800" 
                                                placeholder="e.g., Return in 7 days if fever persists or symptoms worsen" 
                                                value={recordData.followUp} 
                                                onChange={e => setRecordData({...recordData, followUp: e.target.value})} 
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Specialist Referrals</label>
                                            <input 
                                                type="text"
                                                className="w-full p-3.5 bg-slate-50 rounded-2xl border border-slate-200 focus:bg-white focus:border-indigo-500 outline-none font-semibold text-xs text-slate-800" 
                                                placeholder="e.g., Referral to Pulmonology if cough > 3 weeks" 
                                                value={recordData.referrals} 
                                                onChange={e => setRecordData({...recordData, referrals: e.target.value})} 
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Confidential Clinical Observations</label>
                                        <textarea 
                                            className="w-full p-3.5 bg-slate-50 rounded-2xl h-20 font-semibold resize-none outline-none focus:bg-white focus:border-indigo-500 border border-slate-200 text-xs text-slate-800" 
                                            placeholder="Private clinician observations..."
                                            value={recordData.notes} 
                                            onChange={e => setRecordData({...recordData, notes: e.target.value})} 
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section 4: Attachments */}
                            <div className="space-y-3 p-4 bg-slate-50 rounded-2xl border border-slate-200">
                                <label className="text-[10px] font-black text-slate-600 uppercase tracking-widest flex items-center gap-2">
                                    <i className="fa-solid fa-paperclip text-indigo-600"></i>
                                    <span>Supporting Clinical Documents (Scans, Labs, PDF)</span>
                                </label>
                                <input 
                                    type="file" 
                                    multiple
                                    className="w-full p-3 bg-white rounded-xl border border-dashed border-indigo-200 font-bold text-xs text-slate-600 cursor-pointer" 
                                    onChange={e => {
                                        if (e.target.files && e.target.files.length > 0) {
                                            const newFiles = Array.from(e.target.files);
                                            setRecordFiles(prev => [...prev, ...newFiles]);
                                        }
                                    }} 
                                />
                                {recordFiles.length > 0 && (
                                    <div className="space-y-1.5 max-h-32 overflow-y-auto scrollbar-thin mt-2">
                                        <p className="text-[10px] font-black uppercase text-indigo-600 tracking-wider">
                                            {recordFiles.length} file(s) selected:
                                        </p>
                                        {recordFiles.map((file, fIdx) => (
                                            <div key={fIdx} className="flex items-center justify-between p-2 bg-white rounded-xl border border-slate-200 text-xs">
                                                <span className="font-bold text-slate-700 truncate" title={file.name}>
                                                    <i className="fa-solid fa-paperclip text-indigo-500 mr-1.5"></i>
                                                    {file.name} ({(file.size / 1024).toFixed(1)} KB)
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => setRecordFiles(prev => prev.filter((_, i) => i !== fIdx))}
                                                    className="text-rose-500 hover:text-rose-700 ml-2 p-1"
                                                    title="Remove file"
                                                >
                                                    <i className="fa-solid fa-xmark"></i>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="p-4 sm:p-6 md:p-8 bg-slate-50/90 backdrop-blur-md flex gap-3 sm:gap-4 shrink-0 border-t border-slate-100 z-10">
                            <Button onClick={() => setShowRecordModal(false)} variant="ghost" className="flex-1 font-bold py-3 text-sm">
                                Discard
                            </Button>
                            <Button 
                                onClick={submitOnBehalfRecord} 
                                disabled={isSaving || !recordData.diagnosis.trim() || !recordPatientId} 
                                className="flex-[2] py-3 text-sm shadow-lg shadow-indigo-600/20 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
                            >
                                {isSaving ? (
                                    <i className="fa-solid fa-circle-notch fa-spin"></i>
                                ) : (
                                    <i className="fa-solid fa-check-double"></i>
                                )}
                                {isSaving ? 'Saving...' : 'Authorize Clinical Record & Issue Rx'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {showFollowUpModal && (
                <div className="fixed inset-0 z-[500] flex items-center justify-center p-2 sm:p-4 md:p-6 bg-slate-900/60 backdrop-blur-md overflow-y-auto animate-in fade-in duration-300">
                    <div className="bg-white w-full max-w-md rounded-[1.8rem] sm:rounded-[2.5rem] md:rounded-[3rem] shadow-2xl animate-in zoom-in-95 duration-300 max-h-[calc(100dvh-1.25rem)] sm:max-h-[calc(100dvh-2rem)] md:max-h-[85vh] my-auto flex flex-col overflow-hidden border border-slate-100 relative">
                         <div className="p-4 sm:p-6 md:p-8 border-b border-slate-50 bg-slate-50/50 flex justify-between items-center shrink-0">
                            <div>
                                <h3 className="text-lg sm:text-xl md:text-2xl font-black text-slate-900">Follow-up Session</h3>
                                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-0.5">Patient: {selectedPatient?.name}</p>
                            </div>
                            <button onClick={() => setShowFollowUpModal(false)} className="text-slate-300 hover:text-slate-500 transition-colors p-2 rounded-xl"><i className="fa-solid fa-xmark text-xl"></i></button>
                        </div>
                        <div className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6 overflow-y-auto flex-1 scrollbar-thin min-h-0">
                            <div className="bg-emerald-50 p-3.5 sm:p-4 rounded-xl border border-emerald-100">
                                <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Complimentary Session</p>
                                <p className="text-xs font-bold text-emerald-700 mt-1 italic">This follow-up is free of charge for the patient.</p>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Appointment Date</label>
                                <input 
                                    type="date" 
                                    className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-sm sm:text-base" 
                                    value={followUpData.date} 
                                    onChange={e => setFollowUpData({...followUpData, date: e.target.value})} 
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Appointment Time</label>
                                <input 
                                    type="time" 
                                    className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 outline-none font-bold text-sm sm:text-base" 
                                    value={followUpData.time} 
                                    onChange={e => setFollowUpData({...followUpData, time: e.target.value})} 
                                />
                            </div>
                        </div>
                        <div className="p-6 md:p-8 bg-slate-50/90 backdrop-blur-md flex gap-4 shrink-0 border-t border-slate-100 z-10">
                            <Button onClick={() => setShowFollowUpModal(false)} variant="ghost" className="flex-1">Cancel</Button>
                            <Button onClick={handleScheduleFollowUp} disabled={isSaving || !followUpData.date || !followUpData.time} className="flex-[2]">
                                {isSaving ? <i className="fa-solid fa-circle-notch fa-spin"></i> : 'Confirm Free Session'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Pre-Consultation Triage Review Modal */}
            {triageAppointment && (
                <PreConsultationTriageModal
                    appointment={triageAppointment}
                    isOpen={true}
                    isDoctorView={true}
                    onClose={() => setTriageAppointment(null)}
                />
            )}

                        {/* Clinical Record Amendment Modal with Audit Logging */}
            {amendingRecord && (
                <div className="fixed inset-0 z-[550] flex items-center justify-center p-2 sm:p-4 md:p-6 bg-slate-950/80 backdrop-blur-md overflow-y-auto animate-in fade-in duration-300">
                    <div className="bg-white w-full max-w-4xl rounded-[2rem] sm:rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 max-h-[90vh] my-auto flex flex-col border border-slate-100 relative">
                        <div className="p-6 md:p-8 border-b border-slate-100 bg-slate-50/80 flex justify-between items-center shrink-0">
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full bg-amber-500 animate-pulse"></span>
                                    <h3 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight">Amend Clinical Consultation Record</h3>
                                </div>
                                <p className="text-slate-500 font-bold text-xs mt-1">
                                    Date: {amendingRecord.date} • Authored by {amendingRecord.doctorName}
                                </p>
                            </div>
                            <button 
                                onClick={() => setAmendingRecord(null)} 
                                className="w-10 h-10 flex items-center justify-center bg-white rounded-2xl text-slate-400 hover:text-slate-700 hover:shadow-md transition-all border border-slate-200"
                            >
                                <i className="fa-solid fa-xmark text-lg"></i>
                            </button>
                        </div>

                        <div className="p-6 md:p-8 space-y-6 overflow-y-auto flex-1 scrollbar-thin min-h-0">
                            <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200 text-xs text-amber-900 font-medium space-y-1">
                                <p className="font-black flex items-center gap-1.5 uppercase tracking-wider text-[11px] text-amber-800">
                                    <i className="fa-solid fa-shield-halved"></i> Medical Record Audit Compliance
                                </p>
                                <p>All modifications are timestamped, attributed to your doctor account, and stored permanently in the clinical audit history trail for regulatory compliance.</p>
                            </div>

                            {/* Required Reason for Amendment */}
                            <div className="space-y-1.5 p-4 bg-slate-50 rounded-2xl border-2 border-indigo-200">
                                <label className="text-xs font-black uppercase text-indigo-700 tracking-wider flex items-center gap-1.5">
                                    <i className="fa-solid fa-asterisk text-rose-500 text-[10px]"></i> Reason for Clinical Edit / Amendment (Required)
                                </label>
                                <input 
                                    type="text" 
                                    className="w-full p-3.5 bg-white rounded-xl border border-slate-200 focus:border-indigo-500 outline-none font-bold text-sm text-slate-900" 
                                    placeholder="e.g. Updated dosage based on patient lab report / Added revised follow-up instructions"
                                    value={amendReason}
                                    onChange={e => setAmendReason(e.target.value)}
                                />
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-black uppercase text-slate-600 tracking-wider">Clinical Impression (Diagnosis)</label>
                                    <input 
                                        type="text" 
                                        className="w-full p-3.5 bg-slate-50 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none font-bold text-sm text-slate-900" 
                                        value={amendFormData.diagnosis}
                                        onChange={e => setAmendFormData({...amendFormData, diagnosis: e.target.value})}
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-xs font-black uppercase text-slate-600 tracking-wider">Vital Signs</label>
                                    <input 
                                        type="text" 
                                        className="w-full p-3.5 bg-slate-50 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none font-bold text-sm text-slate-900" 
                                        value={amendFormData.vitalSigns}
                                        onChange={e => setAmendFormData({...amendFormData, vitalSigns: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-xs font-black uppercase text-slate-600 tracking-wider">Prescription (Rx) & Dosage</label>
                                <textarea 
                                    className="w-full p-3.5 bg-slate-50 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none font-bold text-sm text-slate-900 h-24 resize-none" 
                                    value={amendFormData.prescription}
                                    onChange={e => setAmendFormData({...amendFormData, prescription: e.target.value})}
                                />
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-black uppercase text-slate-600 tracking-wider">Treatment Plan & Guidance</label>
                                    <textarea 
                                        className="w-full p-3.5 bg-slate-50 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none font-bold text-sm text-slate-900 h-24 resize-none" 
                                        value={amendFormData.treatmentPlan}
                                        onChange={e => setAmendFormData({...amendFormData, treatmentPlan: e.target.value})}
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-xs font-black uppercase text-slate-600 tracking-wider">Follow-up Instructions</label>
                                    <textarea 
                                        className="w-full p-3.5 bg-slate-50 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none font-bold text-sm text-slate-900 h-24 resize-none" 
                                        value={amendFormData.followUpInstructions}
                                        onChange={e => setAmendFormData({...amendFormData, followUpInstructions: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-black uppercase text-slate-600 tracking-wider">Presenting Symptoms</label>
                                    <input 
                                        type="text" 
                                        className="w-full p-3.5 bg-slate-50 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none font-bold text-sm text-slate-900" 
                                        value={amendFormData.presentingSymptoms}
                                        onChange={e => setAmendFormData({...amendFormData, presentingSymptoms: e.target.value})}
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-xs font-black uppercase text-slate-600 tracking-wider">Issued Referrals</label>
                                    <input 
                                        type="text" 
                                        className="w-full p-3.5 bg-slate-50 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none font-bold text-sm text-slate-900" 
                                        value={amendFormData.referralsIssued}
                                        onChange={e => setAmendFormData({...amendFormData, referralsIssued: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-xs font-black uppercase text-slate-600 tracking-wider">Additional Provider Clinical Notes</label>
                                <textarea 
                                    className="w-full p-3.5 bg-slate-50 rounded-xl border border-slate-200 focus:border-indigo-500 outline-none font-bold text-sm text-slate-900 h-20 resize-none" 
                                    value={amendFormData.notes}
                                    onChange={e => setAmendFormData({...amendFormData, notes: e.target.value})}
                                />
                            </div>
                        </div>

                        <div className="p-6 md:p-8 bg-slate-50/90 backdrop-blur-md flex gap-4 shrink-0 border-t border-slate-100 z-10">
                            <Button onClick={() => setAmendingRecord(null)} variant="ghost" className="flex-1 font-bold py-3 text-sm">
                                Cancel
                            </Button>
                            <Button 
                                onClick={handleSaveRecordAmendment} 
                                disabled={isSavingAmendment || !amendReason.trim() || !amendFormData.diagnosis.trim()} 
                                className="flex-[2] py-3 text-sm shadow-lg shadow-indigo-600/20 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
                            >
                                {isSavingAmendment ? (
                                    <i className="fa-solid fa-circle-notch fa-spin"></i>
                                ) : (
                                    <i className="fa-solid fa-cloud-arrow-up"></i>
                                )}
                                {isSavingAmendment ? 'Saving Amendment...' : 'Save & Log Amendment to Audit Trail'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Official Rovita Digital Prescription Modal */}
            {viewingPrescriptionRecord && (
                <DigitalPrescriptionModal
                    record={viewingPrescriptionRecord}
                    onClose={() => setViewingPrescriptionRecord(null)}
                />
            )}

            {/* Medical Record & Document Metadata Modal */}
            {viewingMetadataRecord && (
                <MedicalRecordMetadataModal
                    record={viewingMetadataRecord}
                    isOpen={Boolean(viewingMetadataRecord)}
                    onClose={() => setViewingMetadataRecord(null)}
                    onOpenPrescriptionModal={(rec) => {
                        setViewingMetadataRecord(null);
                        setViewingPrescriptionRecord(rec);
                    }}
                />
            )}
        </div>
    );
};
